// ═══════════════════════════════════════════════════════════════════════
// CLM MISSION CONTROL — App.jsx (VERSION 10.0 COMPLETE)
// Siemens Closed-Loop Manufacturing · End-to-End Design & Tracking Platform
// Version: 10.0 · 15 tabs · 40 interfaces · 27 agents · 76 benefits ·
// Master Architecture · ESB · DFD · Data Contracts · LLD · Sync Guide
// ═══════════════════════════════════════════════════════════════════════

import React, { useState, useCallback, useReducer, useRef, useMemo, useEffect } from "react";
import E2EClm from "./e2eClm";
import VisualSelection from "./visualSelection";
import {
  IFACES,
  GRP_META,
  CX_META,
  MVP_META,
  RISK_META,
  PHASE_META,
  TRIGGER_TYPES,
  TASK_TYPE_C,
  getAllTriggerTypes,
  getTriggerFilterOptions,
  getInterfaceProgress,
  getTotalProgress,
  getInterfacesByPhase,
  getInterfacesByGroup,
  getInterfacesByMVP,
  getInterfacesByRisk,
  getInterfacesByTag,
  getInterfaceDependencies,
  getDependentInterfaces,
  getTotalInterfaces,
  getTotalTasks,
  getTotalTriggers,
  getTotalDoDCriteria,
  getPhaseProgress,
  getGroupProgress,
} from "./IfacesData";
import { 
  PROTOCOL_MATRIX, 
  getProtocolsByInterface, 
  getProtocolCategorySummary,
  getSecurityProfile,
  getProtocolStandard 
} from "./IF_Protocols";
import {
  FilterPanel,
  useFilteredInterfaces,
  filterInterfaces,
} from "./filterSystem";
import { TriggerExplorer } from "./triggerSystem";

// ─── NEW: 8 advanced tabs (slidedeck-derived content as live UI) ──────
import BenefitsTab            from "./benefitsTab";
import AgenticAITab           from "./agenticAITab";
import ESBIntegrationTab      from "./esbIntegrationTab";
import MasterArchitectureTab  from "./masterArchitectureTab";
import UnifiedDFDTab          from "./unifiedDFDTab";
import DataContractsTab       from "./dataContractsTab";
import LLDTab                 from "./lldTab";
import UserSyncGuideTab       from "./userSyncGuideTab";
import InterfaceTaxonomyTab   from "./interfaceTaxonomyTab";
import InterfaceIntelligenceTab from "./interfaceIntelligenceTab";
import ImplementationPlanTab  from "./implementationPlanTab";
import MaturityConfigTab      from "./maturityConfigTab";
import { resolveProfile, MATURITY_LEVELS, COMPLEXITY_LEVELS } from "./maturityModel";
import B2MMLFederationTab     from "./b2mmlFederationTab";
import CLMStudio              from "./CLMStudio";

// ─── v10 cross-cutting infrastructure ──────────────────────────────────
import ErrorBoundary             from "./errorBoundary";
import { NavigationProvider }    from "./navigation";
import { T, FONTS }              from "./theme";

// ═══════════════════════════════════════════════════════════════════════
// STYLES - Complete Enterprise Grade Stylesheet
// ═══════════════════════════════════════════════════════════════════════
const styles = {
  wrap: {
    background: T.bg,
    minHeight: "100vh",
    color: T.text,
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    fontSize: 13,
    lineHeight: 1.5,
  },
  hdr: {
    background: "linear-gradient(180deg, rgba(0,229,255,.07), transparent)",
    borderBottom: `1px solid ${T.border}`,
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 15,
    position: "sticky",
    top: 0,
    zIndex: 100,
    backdropFilter: "blur(10px)",
  },
  h1: {
    fontFamily: "'Orbitron', 'Inter', sans-serif",
    fontSize: 18,
    fontWeight: 900,
    letterSpacing: 1.5,
    background: "linear-gradient(135deg, #ffffff 0%, #00e5ff 50%, #ffd600 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  ctrl: {
    borderBottom: `1px solid ${T.border}`,
    padding: "10px 24px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    background: "rgba(4,13,26,.95)",
    position: "sticky",
    top: 62,
    zIndex: 99,
    backdropFilter: "blur(10px)",
  },
  tab: (active) => ({
    fontFamily: "'Share Tech Mono', 'Inter', monospace",
    fontSize: "10px",
    letterSpacing: 1.5,
    padding: "6px 14px",
    borderRadius: 4,
    cursor: "pointer",
    border: `1px solid ${active ? T.cyan : T.border}`,
    color: active ? T.cyan : T.text2,
    background: active ? `${T.cyan}12` : "transparent",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    transition: "all 0.2s ease",
    fontWeight: active ? 600 : 400,
  }),
  sel: {
    background: T.bg2,
    border: `1px solid ${T.border}`,
    color: T.text,
    fontFamily: FONTS.mono,
    fontSize: "10px",
    padding: "6px 10px",
    borderRadius: 4,
    outline: "none",
    cursor: "pointer",
  },
  inp: {
    background: T.bg2,
    border: `1px solid ${T.border}`,
    color: T.text,
    fontFamily: FONTS.mono,
    fontSize: "10px",
    padding: "6px 12px",
    borderRadius: 4,
    outline: "none",
    width: 220,
    transition: "all 0.2s ease",
  },
  textarea: {
    background: T.bg2,
    border: `1px solid ${T.border}`,
    color: T.text,
    fontFamily: FONTS.mono,
    fontSize: "10px",
    padding: "8px 12px",
    borderRadius: 4,
    outline: "none",
    width: "100%",
    minHeight: 80,
    resize: "vertical",
    transition: "all 0.2s ease",
  },
  card: (color) => ({
    background: T.bg3,
    border: `1px solid ${T.border2}`,
    borderLeft: `3px solid ${color}`,
    borderRadius: 6,
    padding: "12px 14px",
    marginBottom: 8,
    cursor: "pointer",
    transition: "all 0.2s ease",
  }),
  pill: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 20,
    fontFamily: FONTS.mono,
    fontSize: "9px",
    border: `1px solid ${c}40`,
    color: c,
    background: `${c}12`,
    marginRight: 4,
    gap: 4,
  }),
  section: { padding: "20px 24px" },
  tblHdr: {
    background: "#0a1828",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  th: {
    padding: "10px 12px",
    textAlign: "left",
    fontFamily: FONTS.mono,
    fontSize: "9px",
    letterSpacing: 1.5,
    textTransform: "uppercase",
    color: T.text2,
    borderBottom: `1px solid ${T.border}`,
    whiteSpace: "nowrap",
    cursor: "pointer",
    fontWeight: 600,
  },
  td: {
    padding: "10px 12px",
    borderBottom: `1px solid ${T.border2}`,
    verticalAlign: "middle",
    fontSize: 11,
  },
  detailPanel: {
    background: T.bg2,
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    padding: "16px 20px",
    marginTop: 12,
  },
  detailSection: {
    marginBottom: 16,
    borderBottom: `1px solid ${T.border}`,
    paddingBottom: 12,
  },
  detailTitle: {
    fontFamily: FONTS.mono,
    fontSize: "9px",
    letterSpacing: 2,
    textTransform: "uppercase",
    color: T.cyan,
    marginBottom: 10,
    fontWeight: 600,
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 12,
  },
  detailItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "6px 0",
  },
  detailLabel: {
    fontFamily: FONTS.mono,
    fontSize: "9px",
    color: T.text2,
    minWidth: 110,
    fontWeight: 500,
  },
  detailValue: {
    fontSize: "9.5px",
    color: T.text,
    fontFamily: "monospace",
    wordBreak: "break-word",
    flex: 1,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: "9px",
    fontWeight: 600,
    gap: 4,
  },
  progressBar: {
    height: 4,
    background: T.bg3,
    borderRadius: 2,
    overflow: "hidden",
  },
  progressFill: (color, width) => ({
    height: "100%",
    background: color,
    width: `${width}%`,
    borderRadius: 2,
    transition: "width 0.3s ease",
  }),
  kpiCard: {
    background: T.bg2,
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    padding: "12px 16px",
    textAlign: "center",
    minWidth: 80,
  },
  kpiValue: {
    fontFamily: FONTS.header,
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  kpiLabel: {
    fontSize: 8,
    color: T.text2,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginTop: 4,
  },
  protocolBadge: (color) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 6px",
    borderRadius: 3,
    fontFamily: FONTS.mono,
    fontSize: "7px",
    fontWeight: 600,
    background: `${color}15`,
    color: color,
    border: `1px solid ${color}30`,
  }),
  commentBubble: {
    background: `${T.amber}08`,
    border: `1px solid ${T.amber}30`,
    borderRadius: 6,
    padding: "10px 14px",
    marginTop: 10,
    position: "relative",
  },
  commentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
    fontFamily: FONTS.mono,
    fontSize: 8,
    color: T.amber,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  commentInput: {
    background: T.bg3,
    border: `1px solid ${T.border}`,
    color: T.text,
    fontFamily: "'Inter', sans-serif",
    fontSize: "10px",
    padding: "8px 12px",
    borderRadius: 4,
    outline: "none",
    width: "100%",
    minHeight: 60,
    resize: "vertical",
    transition: "all 0.2s ease",
  },
  commentSaveBtn: {
    background: `${T.amber}15`,
    border: `1px solid ${T.amber}40`,
    color: T.amber,
    padding: "4px 12px",
    borderRadius: 4,
    cursor: "pointer",
    fontFamily: FONTS.mono,
    fontSize: 8,
    transition: "all 0.2s ease",
  },
  customAttrRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    padding: "4px 0",
  },
  customAttrInput: {
    background: T.bg3,
    border: `1px solid ${T.border}`,
    color: T.text,
    fontFamily: FONTS.mono,
    fontSize: "9px",
    padding: "4px 8px",
    borderRadius: 3,
    outline: "none",
    flex: 1,
  },
  addBtn: {
    background: `${T.cyan}10`,
    border: `1px solid ${T.cyan}30`,
    color: T.cyan,
    padding: "4px 10px",
    borderRadius: 4,
    cursor: "pointer",
    fontFamily: FONTS.mono,
    fontSize: 8,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  deleteBtn: {
    background: "transparent",
    border: "none",
    color: T.red,
    cursor: "pointer",
    fontSize: 14,
    padding: "0 4px",
    lineHeight: 1,
  },
  commentBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 10,
    background: `${T.amber}15`,
    border: `1px solid ${T.amber}30`,
    color: T.amber,
    fontSize: "8px",
    fontFamily: FONTS.mono,
  },
  remarkIndicator: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: T.amber,
    display: "inline-block",
    marginLeft: 4,
    boxShadow: `0 0 6px ${T.amber}60`,
  },
};

// ═══════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════

function downloadFile(content, filename, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function formatDate(date) {
  return new Date(date).toLocaleString();
}

function getProgressColor(pct) {
  if (pct === 100) return T.success;
  if (pct >= 75) return T.cyan;
  if (pct >= 50) return T.amber;
  if (pct >= 25) return T.orange;
  return T.red;
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

function getProtocolCategoryColor(categoryId) {
  const colorMap = {
    "PLM-TO-MES": T.cyan, "MES-TO-ERP": T.amber, "PLM-TO-ERP": T.blue,
    "MES-SHOP-FLOOR": T.orange, "EDGE-INTEGRATION": T.teal, "CLOUD-ANALYTICS": T.purple,
    "QUALITY-LAB": T.pink, "PROCESS-ENGINEERING": T.gold,
  };
  return colorMap[categoryId] || T.steel;
}

function getProtocolCategoryIcon(categoryId) {
  const iconMap = {
    "PLM-TO-MES": "🏭", "MES-TO-ERP": "💼", "PLM-TO-ERP": "🔗",
    "MES-SHOP-FLOOR": "⚙️", "EDGE-INTEGRATION": "🔌", "CLOUD-ANALYTICS": "☁️",
    "QUALITY-LAB": "🔬", "PROCESS-ENGINEERING": "📐",
  };
  return iconMap[categoryId] || "📡";
}

// ═══════════════════════════════════════════════════════════════════════
// UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════

function Ring({ pct, color, size = 32, strokeWidth = 2.5 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,.07)" strokeWidth={strokeWidth} />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x={size / 2} y={size / 2 + (size === 30 ? 3 : 4)} textAnchor="middle"
        fontFamily={FONTS.mono} fontSize={size === 30 ? 7 : 9} fontWeight={700} fill={color}>{pct}</text>
    </svg>
  );
}

function Pill({ text, color, icon }) {
  return <span style={styles.pill(color)}>{icon && <span style={{ fontSize: 10 }}>{icon}</span>}{text}</span>;
}

function ProtocolBadge({ protocol, compact }) {
  const protocolColors = {
    "REST": T.green, "SOAP": T.blue, "OPC-UA": T.orange, "MQTT": T.purple,
    "iDoc": T.amber, "BAPI": T.teal, "AS2": T.pink, "SFTP": T.steel,
    "PLMXML": T.gold, "OData": T.cyan,
  };
  const color = Object.keys(protocolColors).find(key => protocol?.includes(key))
    ? protocolColors[Object.keys(protocolColors).find(key => protocol?.includes(key))]
    : T.steel;
  return <span style={styles.protocolBadge(color)}>{compact ? protocol?.split(" ")[0] : protocol}</span>;
}

function NISBadge() {
  return <span style={{ ...styles.badge, background: `${T.red}15`, border: `1px solid ${T.red}40`, color: T.red }}>⛔ NOT IN SCOPE</span>;
}

function StatusBadge({ status }) {
  const config = {
    Completed: { color: T.success, icon: "✅" },
    "In Progress": { color: T.cyan, icon: "🔄" },
    "Not Started": { color: T.text2, icon: "⏳" },
    Blocked: { color: T.red, icon: "⚠️" },
  };
  const c = config[status] || { color: T.text2, icon: "📌" };
  return <span style={{ ...styles.badge, background: `${c.color}15`, color: c.color }}>{c.icon} {status}</span>;
}

// ═══════════════════════════════════════════════════════════════════════
// COMMENTS / REMARKS COMPONENT
// ═══════════════════════════════════════════════════════════════════════

function CommentSection({ targetId, targetType, comments, onSaveComment, onDeleteComment }) {
  const [isEditing, setIsEditing] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [author, setAuthor] = useState(() => localStorage.getItem("clm_comment_author") || "");
  
  const existingComments = comments?.[targetId] || [];
  
  const handleSave = () => {
    if (!commentText.trim()) return;
    if (author.trim()) {
      localStorage.setItem("clm_comment_author", author.trim());
    }
    onSaveComment(targetId, {
      text: commentText.trim(),
      author: author.trim() || "Anonymous",
      timestamp: new Date().toISOString(),
      type: targetType,
    });
    setCommentText("");
    setIsEditing(false);
  };
  
  const handleDelete = (commentIndex) => {
    onDeleteComment(targetId, commentIndex);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    }
  };
  
  return (
    <div style={{ marginTop: 8 }}>
      {existingComments.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {existingComments.map((comment, idx) => (
            <div key={idx} style={styles.commentBubble}>
              <div style={styles.commentHeader}>
                <span>💬 {comment.author} · {new Date(comment.timestamp).toLocaleString()}</span>
                <span onClick={() => handleDelete(idx)} style={{ ...styles.deleteBtn, fontSize: 12 }} title="Delete comment">✕</span>
              </div>
              <div style={{ fontSize: 10, color: T.text, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                {comment.text}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {isEditing ? (
        <div style={{ ...styles.commentBubble, borderColor: `${T.cyan}40` }}>
          <div style={{ marginBottom: 6 }}>
            <input
              type="text"
              placeholder="Your name (saved locally)..."
              value={author}
              onChange={e => setAuthor(e.target.value)}
              style={{ ...styles.customAttrInput, fontSize: 9, marginBottom: 6 }}
            />
            <textarea
              placeholder="Add your comment/remark here... (Ctrl+Enter to save)"
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              onKeyDown={handleKeyDown}
              style={styles.commentInput}
              autoFocus
            />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => { setIsEditing(false); setCommentText(""); }} 
              style={{ ...styles.commentSaveBtn, background: "transparent", border: `1px solid ${T.border}`, color: T.text2 }}>
              Cancel
            </button>
            <button onClick={handleSave} style={styles.commentSaveBtn}>
              💾 Save Comment
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setIsEditing(true)} style={{ ...styles.addBtn, marginTop: 4 }}>
          💬 {existingComments.length > 0 ? `Add Comment (${existingComments.length} existing)` : "Add Comment"}
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CUSTOM TRIGGER ATTRIBUTES COMPONENT
// ═══════════════════════════════════════════════════════════════════════

function CustomTriggerAttributes({ triggerId, customAttrs, onAddAttribute, onDeleteAttribute }) {
  const [newAttrKey, setNewAttrKey] = useState("");
  const [newAttrValue, setNewAttrValue] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  
  const attrs = customAttrs?.[triggerId] || [];
  
  const handleAdd = () => {
    if (!newAttrKey.trim() || !newAttrValue.trim()) return;
    onAddAttribute(triggerId, {
      key: newAttrKey.trim(),
      value: newAttrValue.trim(),
      timestamp: new Date().toISOString(),
    });
    setNewAttrKey("");
    setNewAttrValue("");
    setIsAdding(false);
  };
  
  return (
    <div style={{ marginTop: 8 }}>
      {attrs.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9, marginBottom: 8 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.purple}30` }}>
              <th style={{ textAlign: "left", padding: "4px 6px", color: T.purple, fontSize: 8 }}>Attribute</th>
              <th style={{ textAlign: "left", padding: "4px 6px", color: T.purple, fontSize: 8 }}>Value</th>
              <th style={{ textAlign: "center", padding: "4px 6px", color: T.purple, fontSize: 8, width: 30 }}>✕</th>
            </tr>
          </thead>
          <tbody>
            {attrs.map((attr, idx) => (
              <tr key={idx} style={{ borderBottom: `1px solid ${T.border}` }}>
                <td style={{ padding: "4px 6px", fontFamily: "monospace", color: T.cyan, fontSize: 8 }}>{attr.key}</td>
                <td style={{ padding: "4px 6px", fontFamily: "monospace", color: T.green, fontSize: 8 }}>{attr.value}</td>
                <td style={{ textAlign: "center", padding: "4px 6px" }}>
                  <span onClick={() => onDeleteAttribute(triggerId, idx)} style={styles.deleteBtn} title="Delete attribute">✕</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      
      {isAdding ? (
        <div style={styles.customAttrRow}>
          <input type="text" placeholder="Attribute name" value={newAttrKey} onChange={e => setNewAttrKey(e.target.value)} style={{ ...styles.customAttrInput, flex: 1 }} autoFocus />
          <input type="text" placeholder="Value" value={newAttrValue} onChange={e => setNewAttrValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }} style={{ ...styles.customAttrInput, flex: 2 }} />
          <button onClick={handleAdd} style={styles.addBtn}>✓ Add</button>
          <button onClick={() => setIsAdding(false)} style={{ ...styles.addBtn, color: T.text2, borderColor: T.border }}>Cancel</button>
        </div>
      ) : (
        <button onClick={() => setIsAdding(true)} style={{ ...styles.addBtn, marginTop: 4 }}>➕ Add Custom Attribute</button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// INTERFACE COMMENTS INDICATOR
// ═══════════════════════════════════════════════════════════════════════

function InterfaceCommentIndicator({ interfaceId, comments }) {
  const ifaceComments = comments?.[interfaceId] || [];
  const triggerComments = Object.keys(comments || {}).filter(k => k.startsWith(interfaceId + "-TRG")).reduce((sum, k) => sum + (comments[k]?.length || 0), 0);
  const totalComments = ifaceComments.length + triggerComments;
  if (totalComments === 0) return null;
  return <span style={styles.commentBadge} title={`${totalComments} comment(s)`}>💬 {totalComments}</span>;
}

// ═══════════════════════════════════════════════════════════════════════
// TECHNICAL DETAILS SECTION
// ═══════════════════════════════════════════════════════════════════════

function TechnicalDetailSection({ title, details, color }) {
  if (!details || Object.keys(details).length === 0) return null;
  const renderValue = (value, key) => {
    if (value === null || value === undefined) return <span style={{ color: T.text3 }}>Not specified</span>;
    if (typeof value === "object" && !Array.isArray(value)) {
      return <pre style={{ fontSize: 8, margin: 0, color: T.text2, background: T.bg, padding: 6, borderRadius: 4 }}>{JSON.stringify(value, null, 2)}</pre>;
    }
    if (typeof value === "boolean") return <span style={{ color: value ? T.success : T.error }}>{value ? "✓ Enabled" : "✗ Disabled"}</span>;
    if (Array.isArray(value)) return <div>{value.map((v, i) => <div key={i} style={{ fontSize: 9, color: T.text2 }}>• {v}</div>)}</div>;
    return <span style={styles.detailValue}>{String(value)}</span>;
  };
  return (
    <div style={styles.detailSection}>
      <div style={{ ...styles.detailTitle, color: color || T.cyan }}>{title}</div>
      <div style={styles.detailGrid}>
        {Object.entries(details).map(([key, value]) => (
          <div key={key} style={styles.detailItem}>
            <div style={styles.detailLabel}>{key.replace(/([A-Z])/g, ' $1').trim()}:</div>
            <div style={{ flex: 1 }}>{renderValue(value, key)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PROTOCOL DETAILS PANEL
// ═══════════════════════════════════════════════════════════════════════

function ProtocolDetailsPanel({ interfaceId }) {
  const protocolInfo = getProtocolsByInterface(interfaceId);
  if (!protocolInfo) return null;
  const catColor = getProtocolCategoryColor(protocolInfo.category);
  const catIcon = getProtocolCategoryIcon(protocolInfo.category);
  const categoryMeta = PROTOCOL_MATRIX.protocolCategories[protocolInfo.category];
  
  return (
    <div style={{ marginBottom: 16, border: `1px solid ${catColor}25`, borderRadius: 8, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", background: `${catColor}08`, borderBottom: `1px solid ${catColor}20`, display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 18 }}>{catIcon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: FONTS.header, fontSize: 10, fontWeight: 700, color: catColor }}>
            {categoryMeta?.label || protocolInfo.category}
          </div>
          <div style={{ fontSize: 8, color: T.text2, marginTop: 2 }}>{categoryMeta?.description || protocolInfo.context}</div>
        </div>
      </div>
      <div style={{ padding: "12px 14px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 8 }}>
        <div>
          <div style={{ fontFamily: FONTS.mono, fontSize: 8, color: T.text2, textTransform: "uppercase", marginBottom: 6 }}>🔌 Protocols</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 8, color: T.text2, minWidth: 45 }}>Primary:</span>
              <ProtocolBadge protocol={protocolInfo.primaryProtocol} />
            </div>
            {protocolInfo.secondaryProtocol && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 8, color: T.text2, minWidth: 45 }}>Secondary:</span>
                {Array.isArray(protocolInfo.secondaryProtocol)
                  ? protocolInfo.secondaryProtocol.map((p, i) => <ProtocolBadge key={i} protocol={p} compact />)
                  : <ProtocolBadge protocol={protocolInfo.secondaryProtocol} />}
              </div>
            )}
          </div>
        </div>
        <div>
          <div style={{ fontFamily: FONTS.mono, fontSize: 8, color: T.text2, textTransform: "uppercase", marginBottom: 6 }}>📄 Data Format</div>
          <div style={{ fontSize: 9, color: T.text, fontFamily: "monospace" }}>{protocolInfo.dataFormat}</div>
        </div>
        <div>
          <div style={{ fontFamily: FONTS.mono, fontSize: 8, color: T.text2, textTransform: "uppercase", marginBottom: 6 }}>⚡ Sync Strategy</div>
          <div style={{ fontSize: 9, color: T.amber }}>{protocolInfo.syncStrategy}</div>
        </div>
        {protocolInfo.latencySLA && (
          <div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 8, color: T.text2, textTransform: "uppercase", marginBottom: 6 }}>⏱️ Latency SLA</div>
            <div style={{ fontSize: 8, color: T.text }}>
              <div>Typical: <span style={{ color: T.green }}>{protocolInfo.latencySLA.typical}</span></div>
              {protocolInfo.latencySLA.max && <div>Max: <span style={{ color: T.amber }}>{protocolInfo.latencySLA.max}</span></div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TASK DRAWER (Enhanced with Comments, Protocol Details, Custom Attributes)
// ═══════════════════════════════════════════════════════════════════════

function TaskDrawer({ iface, state, dispatch }) {
  const nis = state.nisState[iface.id];
  const grpColor = GRP_META[iface.grp]?.color || T.steel;
  const [expandedTriggers, setExpandedTriggers] = useState(new Set());
  
  const toggleTrigger = (triggerId) => {
    const newSet = new Set(expandedTriggers);
    if (newSet.has(triggerId)) newSet.delete(triggerId);
    else newSet.add(triggerId);
    setExpandedTriggers(newSet);
  };
  
  const handleSaveComment = (targetId, comment) => {
    dispatch({ type: "ADD_COMMENT", targetId, comment });
  };
  
  const handleDeleteComment = (targetId, commentIndex) => {
    dispatch({ type: "DELETE_COMMENT", targetId, commentIndex });
  };
  
  const handleAddTriggerAttribute = (triggerId, attr) => {
    dispatch({ type: "ADD_TRIGGER_ATTRIBUTE", triggerId, attr });
  };
  
  const handleDeleteTriggerAttribute = (triggerId, attrIndex) => {
    dispatch({ type: "DELETE_TRIGGER_ATTRIBUTE", triggerId, attrIndex });
  };
  
  const hasIfaceComments = (state.comments?.[iface.id]?.length || 0) > 0;
  
  return (
    <div style={{ background: T.bg2, padding: "16px 20px" }}>
      <ProtocolDetailsPanel interfaceId={iface.id} />
      
      <div style={{ fontSize: 11, color: T.text2, lineHeight: 1.6, marginBottom: 16, padding: "12px 16px", borderLeft: `3px solid ${grpColor}`, background: `${grpColor}08`, borderRadius: 6 }}>
        <strong style={{ color: grpColor }}>📌 About:</strong> {iface.about}
      </div>
      
      {iface.integrationStructure && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ ...styles.detailTitle, color: T.cyan }}>🔧 INTEGRATION ARCHITECTURE</div>
          <div style={styles.detailPanel}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
              <Pill text={`Architecture: ${iface.integrationStructure.architecture}`} color={T.cyan} />
              <ProtocolBadge protocol={iface.protocol} />
              <Pill text={`Frequency: ${iface.freq}`} color={T.amber} />
              <Pill text={`Data Flow: ${iface.integrationStructure.dataFlow}`} color={T.green} />
            </div>
            <div style={{ fontSize: 10, color: T.text2, marginBottom: 8 }}><strong>Components:</strong></div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
              {iface.integrationStructure.components?.map((comp, idx) => (
                <div key={idx} style={{ background: `${T.blue}15`, padding: "4px 10px", borderRadius: 4, fontSize: 9, color: T.blue }}>
                  <strong>{comp.name}:</strong> {comp.role}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {iface.implementationOrder && iface.implementationOrder.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ ...styles.detailTitle, color: T.gold }}>📋 IMPLEMENTATION ORDER</div>
          <div style={styles.detailPanel}>
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 10, color: T.text2, lineHeight: 1.7 }}>
              {iface.implementationOrder.map((item, idx) => {
                if (typeof item === 'string') return <li key={idx}>{item}</li>;
                if (typeof item === 'object' && item.action) {
                  return (
                    <li key={idx}>
                      <strong style={{ color: T.cyan }}>Step {item.step}:</strong> {item.action}
                      {item.duration && <span style={{ color: T.text3, marginLeft: 8 }}>({item.duration})</span>}
                      {item.owner && <span style={{ color: T.amber, marginLeft: 8 }}>Owner: {item.owner}</span>}
                      {item.status && <span style={{ color: item.status === 'completed' ? T.success : T.warning, marginLeft: 8 }}>Status: {item.status}</span>}
                    </li>
                  );
                }
                return <li key={idx}>{JSON.stringify(item)}</li>;
              })}
            </ol>
          </div>
        </div>
      )}
      
      {/* TriggerExplorer always renders — it synthesizes an auto-derived
          direct trigger for interfaces that don't define one explicitly. */}
      <div style={{ marginBottom: 20 }}>
        <TriggerExplorer
          iface={iface}
          state={state}
          dispatch={dispatch}
          CommentSection={CommentSection}
          CustomTriggerAttributes={CustomTriggerAttributes}
        />
      </div>
      
      {nis && (
        <div style={{ background: `${T.red}10`, border: `1px solid ${T.red}30`, borderRadius: 6, padding: "10px 14px", marginBottom: 16 }}>
          <span style={{ color: T.red, fontFamily: FONTS.mono, fontSize: 10 }}>⛔ Interface marked NOT IN SCOPE — DoD items disabled.</span>
        </div>
      )}
      
      <div style={{ marginBottom: 16 }}>
        <div style={{ ...styles.detailTitle, color: T.green }}>✅ TASKS & DoD CRITERIA</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {iface.tasks?.map((task, ti) => {
            const tcol = TASK_TYPE_C[task.type] || T.steel;
            const completedCount = task.dod.filter((_, di) => state.dodState[`${task.id}-${di}`]).length;
            const totalCount = task.dod.length;
            const progressPct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
            
            return (
              <div key={task.id} style={{ background: T.bg3, border: `1px solid ${T.border2}`, borderRadius: 8, overflow: "hidden", opacity: nis ? 0.5 : 1 }}>
                <div style={{ padding: "12px 14px", background: `${tcol}10`, borderBottom: `1px solid ${tcol}30`, display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: tcol, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ color: "#000", fontFamily: FONTS.header, fontSize: 10, fontWeight: 700 }}>{ti + 1}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>{task.name}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Pill text={task.type} color={tcol} />
                      <Pill text={`${task.hours}h`} color={T.purple} />
                      <Pill text={`${CX_META[task.cx]?.l} complexity`} color={CX_META[task.cx]?.c} />
                      <Pill text={`${completedCount}/${totalCount} DoD`} color={progressPct === 100 ? T.success : T.amber} />
                    </div>
                  </div>
                  <Ring pct={Math.round(progressPct)} color={progressPct === 100 ? T.success : tcol} size={32} />
                </div>
                <div style={{ padding: "10px 12px" }}>
                  {task.dod.map((dodText, di) => {
                    const key = `${task.id}-${di}`;
                    const isDone = !!state.dodState[key];
                    return (
                      <div key={di} onClick={() => !nis && dispatch({ type: "TOGGLE_DOD", taskId: task.id, dodIdx: di })}
                        style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "6px 8px", borderBottom: `1px solid ${T.border}`, cursor: nis ? "not-allowed" : "pointer", transition: "background 0.15s ease" }}
                        onMouseEnter={e => !nis && (e.currentTarget.style.background = `${T.bg2}`)}
                        onMouseLeave={e => !nis && (e.currentTarget.style.background = "transparent")}>
                        <div style={{ width: 16, height: 16, borderRadius: 3, border: `1.5px solid ${isDone ? T.success : T.border}`, background: isDone ? T.success : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                          {isDone && <span style={{ color: "#000", fontSize: 10 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 10, color: isDone ? T.text3 : T.text2, lineHeight: 1.4, textDecoration: isDone ? "line-through" : "none", flex: 1 }}>{dodText}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {iface.apis && iface.apis.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...styles.detailTitle, color: T.blue }}>🔌 API SPECIFICATIONS</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {iface.apis.map((api, idx) => (
              <div key={idx} style={{ background: T.bg, border: `1px solid ${T.blue}25`, borderRadius: 6, padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                  <span style={{ fontFamily: "monospace", fontSize: 9, padding: "2px 8px", background: `${T.blue}25`, borderRadius: 4, color: T.blue, fontWeight: 600 }}>{api.method}</span>
                  <span style={{ fontWeight: 600, fontSize: 11, color: T.text }}>{api.name}</span>
                </div>
                <div style={{ fontSize: 10, color: T.text2, marginBottom: 6 }}>{api.purpose}</div>
                <div style={{ marginBottom: 4, fontSize: 9 }}>
                  <span style={{ color: T.text2 }}>URL: </span>
                  <span style={{ color: T.cyan, fontFamily: "monospace" }}>{api.url}</span>
                </div>
                {api.sample && (
                  <pre style={{ background: T.bg2, padding: 10, borderRadius: 6, fontSize: 8.5, overflowX: "auto", color: T.text2, fontFamily: "monospace", border: `1px solid ${T.border}` }}>{api.sample}</pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12, marginTop: 8 }}>
        <div style={{ ...styles.detailTitle, color: T.amber }}>💬 INTERFACE COMMENTS & REMARKS {hasIfaceComments ? `(${state.comments?.[iface.id]?.length || 0})` : ""}</div>
        <CommentSection targetId={iface.id} targetType="interface" comments={state.comments} onSaveComment={handleSaveComment} onDeleteComment={handleDeleteComment} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// FILTER PANEL — imported from ./filterSystem
// ═══════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
// TABLE TAB (Enhanced with Comment Indicators)
// ═══════════════════════════════════════════════════════════════════════

function TableTab({ state, dispatch, selectedInterface }) {
  // Auto-scroll to selected interface
  const selectedRowRef = useRef(null);
  useEffect(() => {
    if (selectedInterface && selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedInterface]);

  const filteredFromGlobal = useFilteredInterfaces(state);
  const search = state.search || "";
  const sortBy = state.sortBy || "seq";

  const filteredData = useMemo(() => {
    const data = [...filteredFromGlobal];
    data.sort((a, b) => {
      if (sortBy === "seq") return a.seq - b.seq;
      if (sortBy === "effort") return b.effort - a.effort;
      if (sortBy === "complexity") return b.complexity - a.complexity;
      if (sortBy === "risk") return b.risk - a.risk;
      if (sortBy === "name") return a.name.localeCompare(b.name);
      return 0;
    });
    return data;
  }, [filteredFromGlobal, sortBy]);
  
  const columns = ["#", "Interface", "From → To", "Group", "MVP", "Phase", "Cx", "Effort", "Risk", "Protocol", "Triggers", "Progress", "Comments", "NIS", ""];
  
  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ padding: "12px 20px", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: T.bg2, padding: "4px 12px", borderRadius: 20, border: `1px solid ${T.border}` }}>
          <span style={{ color: T.text2, fontSize: 12 }}>🔍</span>
          <input type="text" placeholder="Search by ID, name, or business object..." value={search}
            onChange={e => dispatch({ type: "SET_SEARCH", value: e.target.value })}
            style={{ background: "transparent", border: "none", color: T.text, fontSize: 11, padding: "6px 0", outline: "none", width: 250 }} />
        </div>
        <select value={sortBy} onChange={e => dispatch({ type: "SET_SORT", value: e.target.value })} style={styles.sel}>
          <option value="seq">Sort by Sequence</option>
          <option value="effort">Sort by Effort</option>
          <option value="complexity">Sort by Complexity</option>
          <option value="risk">Sort by Risk</option>
          <option value="name">Sort by Name</option>
        </select>
        <div style={{ marginLeft: "auto", fontSize: 11, color: T.text2, background: T.bg2, padding: "4px 12px", borderRadius: 20 }}>
          {filteredData.length} of {IFACES.length} interfaces
        </div>
      </div>
      
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1400 }}>
        <thead style={styles.tblHdr}>
          <tr>{columns.map(col => <th key={col} style={styles.th}>{col}</th>)}</tr>
        </thead>
        <tbody>
          {filteredData.map(iface => {
            const grp = GRP_META[iface.grp] || { color: T.steel, label: iface.grp };
            const progress = getInterfaceProgress(iface, state.dodState, state.nisState);
            const isExpanded = state.expanded.has(iface.id);
            const isSelected = selectedInterface === iface.id;
            const triggerCount = iface.triggers?.length || 0;
            const protocolInfo = getProtocolsByInterface(iface.id);
            const triggerCommentKeys = iface.triggers?.map(t => `${iface.id}-TRG-${t.id}`) || [];
            const totalTriggerComments = triggerCommentKeys.reduce((sum, k) => sum + (state.comments?.[k]?.length || 0), 0);
            const totalComments = (state.comments?.[iface.id]?.length || 0) + totalTriggerComments;
            
            return (
              <React.Fragment key={iface.id}>
                <tr ref={isSelected ? selectedRowRef : null}
                  onClick={() => dispatch({ type: "TOGGLE_EXPAND", id: iface.id })}
                  style={{ cursor: "pointer", background: isExpanded ? `${grp.color}08` : (isSelected ? `${T.gold}15` : "transparent"), opacity: progress.nis ? 0.55 : 1, borderLeft: isSelected ? `3px solid ${T.gold}` : "none" }}>
                  <td style={styles.td}><span style={{ fontFamily: FONTS.header, fontSize: 10, color: T.text2 }}>{String(iface.seq).padStart(2, "0")}</span></td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: 600, borderLeft: `2px solid ${grp.color}`, paddingLeft: 8, marginBottom: 4 }}>{iface.name}</div>
                    <div style={{ fontSize: 8.5, color: T.text2, fontFamily: "monospace" }}>{iface.bo?.substring(0, 50)}...</div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: 9, color: T.text2 }}>{iface.from}</div>
                    <div style={{ fontSize: 9, color: T.cyan }}>→ {iface.to}</div>
                  </td>
                  <td style={styles.td}><Pill text={iface.grp} color={grp.color} /></td>
                  <td style={styles.td}><span style={{ color: MVP_META[iface.mvp]?.c, fontWeight: 600 }}>MVP-{iface.mvp}</span></td>
                  <td style={styles.td}>{iface.phase}</td>
                  <td style={styles.td}><span style={{ color: CX_META[iface.complexity]?.c }}>{CX_META[iface.complexity]?.l}</span></td>
                  <td style={styles.td}><span style={{ color: T.purple, fontWeight: 600 }}>{iface.effort}d</span></td>
                  <td style={styles.td}><span style={{ color: RISK_META[iface.risk]?.c }}>{RISK_META[iface.risk]?.l}</span></td>
                  <td style={styles.td}>{protocolInfo && <ProtocolBadge protocol={protocolInfo.primaryProtocol} compact />}</td>
                  <td style={styles.td}>{triggerCount > 0 ? <span style={{ color: T.pink, fontWeight: 600 }}>{triggerCount}</span> : <span style={{ color: T.text3 }}>—</span>}</td>
                  <td style={styles.td}>
                    {progress.nis ? <NISBadge /> : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Ring pct={progress.pct} color={getProgressColor(progress.pct)} size={28} />
                        <span style={{ fontSize: 10, fontWeight: 600, color: getProgressColor(progress.pct) }}>{progress.pct}%</span>
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <InterfaceCommentIndicator interfaceId={iface.id} comments={state.comments} />
                    {totalTriggerComments > 0 && (<span style={{ ...styles.commentBadge, fontSize: 7, marginLeft: 4 }}>⚡{totalTriggerComments}</span>)}
                  </td>
                  <td style={styles.td}>
                    <button onClick={(e) => { e.stopPropagation(); dispatch({ type: "TOGGLE_NIS", ifaceId: iface.id }); }}
                      style={{ background: progress.nis ? `${T.red}15` : "transparent", border: `1px solid ${progress.nis ? T.red : T.border}`, color: progress.nis ? T.red : T.text2, padding: "3px 8px", borderRadius: 4, fontSize: 8, cursor: "pointer" }}>
                      {progress.nis ? "⛔ NIS" : "Mark NIS"}
                    </button>
                  </td>
                  <td style={styles.td}>
                    <span style={{ color: grp.color, fontSize: 14, display: "inline-block", transform: isExpanded ? "rotate(90deg)" : "none", transition: "transform 0.2s ease" }}>▸</span>
                  </td>
                </tr>
                {isExpanded && (
                  <tr><td colSpan={15} style={{ padding: 0 }}><TaskDrawer iface={iface} state={state} dispatch={dispatch} /></td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// KANBAN TAB
// ═══════════════════════════════════════════════════════════════════════

function KanbanTab({ state, dispatch }) {
  const filteredFromGlobal = useFilteredInterfaces(state);
  const mvps = [
    { level: 1, label: "MVP-1: Must Have", description: "Go-live Blockers", color: MVP_META[1].c },
    { level: 2, label: "MVP-2: Should Have", description: "Phase 2 Post Go-live", color: MVP_META[2].c },
    { level: 3, label: "MVP-3: Could Have", description: "Advanced Capabilities", color: MVP_META[3].c },
  ];
  
  const getColumnProgress = (ifaces) => {
    let total = 0, done = 0;
    ifaces.forEach(iface => {
      const p = getInterfaceProgress(iface, state.dodState, state.nisState);
      total += p.total; done += p.done;
    });
    return total > 0 ? Math.round((done / total) * 100) : 0;
  };
  
  return (
    // FIX: Added overflowY: "auto" to enable scrolling
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, height: "calc(100vh - 108px)", overflow: "hidden", overflowY: "auto" }}>
      {mvps.map(mvp => {
        const ifaces = filteredFromGlobal.filter(f => f.mvp === mvp.level && !state.nisState[f.id]);
        const nisIfaces = filteredFromGlobal.filter(f => f.mvp === mvp.level && state.nisState[f.id]);
        const progress = getColumnProgress(ifaces);
        
        return (
          <div key={mvp.level} style={{ display: "flex", flexDirection: "column", height: "100%", borderRight: `1px solid ${T.border}`, background: T.bg2 }}>
            {/* Column Header */}
            <div style={{ padding: "16px", background: `${mvp.color}10`, borderBottom: `2px solid ${mvp.color}`, flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontFamily: FONTS.header, fontSize: 14, fontWeight: 700, color: mvp.color }}>{mvp.label}</span>
                <span style={{ fontSize: 20, fontWeight: 700, color: mvp.color }}>{ifaces.length + nisIfaces.length}</span>
              </div>
              <div style={{ fontSize: 9, color: T.text2, marginBottom: 8 }}>{mvp.description}</div>
              <div style={{ height: 4, background: T.bg3, borderRadius: 2 }}>
                <div style={{ height: "100%", width: `${progress}%`, background: mvp.color, borderRadius: 2 }} />
              </div>
              <div style={{ fontSize: 8, color: T.text2, marginTop: 4 }}>{progress}% complete</div>
            </div>
            
            {/* Column Content - This already has overflow-y: auto on each column */}
            <div style={{ flex: 1, overflowY: "auto", padding: "12px" }}>
              {/* Active Interfaces */}
              {ifaces.map(iface => {
                const grp = GRP_META[iface.grp] || { color: T.steel };
                const prog = getInterfaceProgress(iface, state.dodState, state.nisState);
                const triggerCount = iface.triggers?.length || 0;
                
                return (
                  <div key={iface.id} onClick={() => { dispatch({ type: "SET_TAB", tab: "table" }); dispatch({ type: "TOGGLE_EXPAND", id: iface.id }); }}
                    style={{ ...styles.card(grp.color), marginBottom: 10, cursor: "pointer" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 4 }}>{iface.name}</div>
                        <div style={{ fontSize: 8, color: T.text2, fontFamily: "monospace" }}>{iface.from.split(' ')[0]} → {iface.to.split(' ')[0]}</div>
                      </div>
                      <Ring pct={prog.pct} color={prog.pct === 100 ? T.success : grp.color} size={32} />
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                      <Pill text={iface.grp} color={grp.color} />
                      <Pill text={`${iface.effort}d`} color={T.purple} />
                      {triggerCount > 0 && <Pill text={`${triggerCount} triggers`} color={T.pink} />}
                    </div>
                    <div style={styles.progressBar}>
                      <div style={styles.progressFill(prog.pct === 100 ? T.success : grp.color, prog.pct)} />
                    </div>
                  </div>
                );
              })}
              
              {/* NIS Interfaces */}
              {nisIfaces.length > 0 && (
                <div style={{ marginTop: 12, borderTop: `1px dashed ${T.border}`, paddingTop: 10 }}>
                  <div style={{ fontSize: 9, color: T.text2, marginBottom: 8 }}>⛔ Not In Scope ({nisIfaces.length})</div>
                  {nisIfaces.map(iface => (
                    <div key={iface.id} style={{ ...styles.card(T.red), opacity: 0.6, marginBottom: 6 }}>
                      <div style={{ fontWeight: 500, fontSize: 10 }}>{iface.name}</div>
                      <div style={{ fontSize: 8, color: T.text2 }}>{iface.grp} · {iface.from.split(' ')[0]} → {iface.to.split(' ')[0]}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════
// RISK MATRIX TAB
// ═══════════════════════════════════════════════════════════════════════

function RiskMatrixTab({ state, dispatch }) {
  const filteredFromGlobal = useFilteredInterfaces(state);
  const effortRanges = [
    { label: "Quick (≤4d)", min: 0, max: 4, color: T.green },
    { label: "Medium (5-8d)", min: 5, max: 8, color: T.amber },
    { label: "Long (9-12d)", min: 9, max: 12, color: T.orange },
    { label: "Epic (≥13d)", min: 13, max: 999, color: T.red },
  ];
  
  const riskLevels = [
    { level: 4, label: "Critical", color: T.red },
    { level: 3, label: "High", color: T.orange },
    { level: 2, label: "Medium", color: T.gold },
    { level: 1, label: "Low", color: T.green },
  ];
  
  return (
    <div style={{ padding: "20px 24px" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 14, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>Risk vs Effort Matrix</div>
        <div style={{ fontSize: 10, color: T.text2 }}>Click any bubble to view interface details</div>
      </div>
      
      <div style={{ overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "100px repeat(4, 1fr)", gap: 8, minWidth: 700 }}>
          <div style={{ padding: "8px", fontWeight: 600, color: T.text2, fontSize: 9 }}>Risk ↓ / Effort →</div>
          {effortRanges.map(range => (<div key={range.label} style={{ textAlign: "center", padding: "8px", background: T.bg2, borderRadius: 6, fontSize: 9, fontWeight: 600, color: range.color }}>{range.label}</div>))}
          {riskLevels.map(risk => (
            <React.Fragment key={risk.level}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "8px", background: `${risk.color}15`, borderRadius: 6, fontWeight: 600, color: risk.color, fontSize: 10 }}>{risk.label}</div>
              {effortRanges.map(range => {
                const interfaces = filteredFromGlobal.filter(f => f.risk === risk.level && f.effort >= range.min && f.effort <= range.max && !state.nisState[f.id]);
                const nisInterfaces = filteredFromGlobal.filter(f => f.risk === risk.level && f.effort >= range.min && f.effort <= range.max && state.nisState[f.id]);
                return (
                  <div key={`${risk.level}-${range.label}`} style={{ background: `${risk.color}08`, border: `1px solid ${risk.color}20`, borderRadius: 8, padding: "8px", minHeight: 120 }}>
                    {interfaces.map(iface => {
                      const grp = GRP_META[iface.grp] || { color: T.steel };
                      const prog = getInterfaceProgress(iface, state.dodState, state.nisState);
                      const totalComments = (state.comments?.[iface.id]?.length || 0);
                      return (
                        <div key={iface.id} onClick={() => { dispatch({ type: "SET_TAB", tab: "table" }); dispatch({ type: "TOGGLE_EXPAND", id: iface.id }); }}
                          style={{ marginBottom: 6, padding: "6px 8px", background: T.bg3, borderRadius: 4, cursor: "pointer", borderLeft: `2px solid ${grp.color}`, display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 9, fontWeight: 500, marginBottom: 2 }}>{iface.name.length > 30 ? iface.name.substring(0, 30) + "…" : iface.name}</div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ fontSize: 7, color: T.text2 }}>{iface.grp}</span>
                              <span style={{ fontSize: 8, color: prog.pct === 100 ? T.success : grp.color }}>{prog.pct}%</span>
                            </div>
                          </div>
                          {totalComments > 0 && <span style={{ ...styles.commentBadge, fontSize: 6 }}>💬</span>}
                        </div>
                      );
                    })}
                    {nisInterfaces.map(iface => (
                      <div key={iface.id} style={{ marginBottom: 4, padding: "4px 6px", background: `${T.red}10`, borderRadius: 4, opacity: 0.6 }}>
                        <div style={{ fontSize: 8 }}>⛔ {iface.name.length > 30 ? iface.name.substring(0, 30) + "…" : iface.name}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TIMELINE TAB
// ═══════════════════════════════════════════════════════════════════════

function TimelineTab({ state, dispatch }) {
  const filteredFromGlobal = useFilteredInterfaces(state);
  const phases = [1, 2, 3, 4, 5, 6, 7, 8];
  
  const getPhaseTotalEffort = (phase) => {
    return IFACES.filter(f => f.phase === phase).reduce((sum, f) => sum + f.effort, 0);
  };
  
  const getPhaseProgressPct = (phase) => {
    // getPhaseProgress signature is (phaseNum, dodState, nisState) and returns
    // an object { done, total, pct, count }; the timeline only needs the pct.
    return getPhaseProgress(phase, state.dodState, state.nisState).pct;
  };
  
  return (
    <div style={{ padding: "20px 24px" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 14, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>Implementation Timeline</div>
        <div style={{ fontSize: 10, color: T.text2 }}>8 phases · {IFACES.reduce((sum, f) => sum + f.effort, 0)} person-days</div>
      </div>
      
      <div style={{ position: "relative", paddingLeft: 30 }}>
        <div style={{ position: "absolute", left: 15, top: 0, bottom: 0, width: 2, background: T.border }} />
        {phases.map(phase => {
          const phaseMeta = PHASE_META[phase];
          const ifaces = filteredFromGlobal.filter(f => f.phase === phase);
          const activeIfaces = ifaces.filter(f => !state.nisState[f.id]);
          const nisIfaces = ifaces.filter(f => state.nisState[f.id]);
          const progress = getPhaseProgressPct(phase);
          const totalEffort = getPhaseTotalEffort(phase);
          if (ifaces.length === 0) return null;
          
          return (
            <div key={phase} style={{ position: "relative", marginBottom: 24 }}>
              <div style={{ position: "absolute", left: -22, top: 8, width: 16, height: 16, borderRadius: "50%", background: phaseMeta.c, border: `2px solid ${T.bg}`, zIndex: 2 }} />
              <div style={{ marginBottom: 12, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <div style={{ background: `${phaseMeta.c}15`, padding: "4px 12px", borderRadius: 20 }}>
                  <span style={{ fontFamily: FONTS.header, fontSize: 12, fontWeight: 700, color: phaseMeta.c }}>Phase {phase}</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.text }}>{phaseMeta.l}</span>
                <span style={{ fontSize: 9, color: T.text2 }}>{activeIfaces.length} active · {nisIfaces.length} NIS · {totalEffort} days</span>
                <div style={{ flex: 1, maxWidth: 200 }}>
                  <div style={styles.progressBar}><div style={styles.progressFill(progress === 100 ? T.success : phaseMeta.c, progress)} /></div>
                </div>
                <span style={{ fontSize: 9, color: progress === 100 ? T.success : phaseMeta.c }}>{progress}%</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginLeft: 20 }}>
                {activeIfaces.map(iface => {
                  const grp = GRP_META[iface.grp] || { color: T.steel };
                  const prog = getInterfaceProgress(iface, state.dodState, state.nisState);
                  const totalComments = (state.comments?.[iface.id]?.length || 0);
                  return (
                    <div key={iface.id} onClick={() => { dispatch({ type: "SET_TAB", tab: "table" }); dispatch({ type: "TOGGLE_EXPAND", id: iface.id }); }}
                      style={{ width: 240, background: T.bg3, border: `1px solid ${grp.color}30`, borderRadius: 8, padding: "10px 12px", cursor: "pointer" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 8, color: grp.color }}>{iface.id}</span>
                          {totalComments > 0 && <span style={{ ...styles.commentBadge, fontSize: 6 }}>💬</span>}
                        </div>
                        <Ring pct={prog.pct} color={prog.pct === 100 ? T.success : grp.color} size={24} />
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 500, marginBottom: 4, lineHeight: 1.3 }}>{iface.name}</div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                        <span style={{ fontSize: 7, color: T.text2 }}>{iface.grp}</span>
                        <span style={{ fontSize: 7, color: T.purple }}>{iface.effort}d</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PROTOCOL MATRIX TAB
// ═══════════════════════════════════════════════════════════════════════

function ProtocolMatrixTab({ state, dispatch }) {
  const categorySummary = getProtocolCategorySummary();
  const securityProfile = getSecurityProfile();
  const [selectedCategory, setSelectedCategory] = useState(null);

  const filteredFromGlobal = useFilteredInterfaces(state);
  const filteredIds = useMemo(
    () => new Set(filteredFromGlobal.map(f => f.id)),
    [filteredFromGlobal]
  );

  const filteredInterfaces = (selectedCategory
    ? PROTOCOL_MATRIX.interfaceProtocolMap.filter(i => i.category === selectedCategory)
    : PROTOCOL_MATRIX.interfaceProtocolMap
  ).filter(i => filteredIds.has(i.interfaceId));
  
  return (
    <div style={{ padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 14, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>🔌 Comprehensive Protocol Matrix</div>
        <div style={{ fontSize: 10, color: T.text2 }}>8 Protocol Categories · 40 Interfaces · IEC 62443 SL2 Security</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10, marginBottom: 20 }}>
        {categorySummary.map(cat => {
          const color = getProtocolCategoryColor(cat.id);
          const icon = getProtocolCategoryIcon(cat.id);
          const isSelected = selectedCategory === cat.id;
          return (
            <div key={cat.id} onClick={() => setSelectedCategory(isSelected ? null : cat.id)}
              style={{ background: isSelected ? `${color}10` : T.bg2, border: `1px solid ${isSelected ? color : T.border}40`, borderRadius: 8, padding: "14px 16px", cursor: "pointer", transition: "all 0.2s ease" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 20 }}>{icon}</span>
                <div>
                  <div style={{ fontFamily: FONTS.header, fontSize: 10, fontWeight: 700, color }}>{cat.label}</div>
                  <div style={{ fontSize: 8, color: T.text2 }}>{cat.interfaceCount} interfaces</div>
                </div>
              </div>
              <div style={{ fontSize: 8, color: T.text2, lineHeight: 1.4, marginBottom: 6 }}>{cat.description}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {cat.primaryProtocols.map((p, i) => <ProtocolBadge key={i} protocol={p} compact />)}
              </div>
              <div style={{ fontSize: 7, color, marginTop: 6, fontFamily: FONTS.mono }}>SLA: {cat.latencySLA.typical}</div>
            </div>
          );
        })}
      </div>

      <div style={{ overflowX: "auto", marginBottom: 20 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
          <thead style={styles.tblHdr}>
            <tr><th style={styles.th}>ID</th><th style={styles.th}>Interface</th><th style={styles.th}>Context</th><th style={styles.th}>Primary Protocol</th><th style={styles.th}>Secondary</th><th style={styles.th}>Sync Strategy</th><th style={styles.th}>SLA</th><th style={styles.th}>Security</th></tr>
          </thead>
          <tbody>
            {filteredInterfaces.map(iface => {
              const catColor = getProtocolCategoryColor(iface.category);
              return (
                <tr key={iface.interfaceId} style={{ cursor: "pointer", borderBottom: `1px solid ${T.border2}` }}
                  onMouseEnter={e => e.currentTarget.style.background = T.bg3}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ ...styles.td, fontFamily: FONTS.header, fontSize: 9, color: catColor }}>{iface.interfaceId}</td>
                  <td style={{ ...styles.td, fontSize: 10 }}><div style={{ fontWeight: 600 }}>{iface.name}</div></td>
                  <td style={{ ...styles.td, fontSize: 8, color: T.text2 }}>{iface.context}</td>
                  <td style={styles.td}><ProtocolBadge protocol={iface.primaryProtocol} /></td>
                  <td style={styles.td}>
                    {Array.isArray(iface.secondaryProtocol) ? iface.secondaryProtocol.map((p, i) => <ProtocolBadge key={i} protocol={p} compact />) : iface.secondaryProtocol && <ProtocolBadge protocol={iface.secondaryProtocol} compact />}
                  </td>
                  <td style={{ ...styles.td, fontSize: 8, color: T.amber }}>{iface.syncStrategy?.substring(0, 30)}...</td>
                  <td style={{ ...styles.td, fontSize: 8, color: T.green }}>{iface.latencySLA?.typical}</td>
                  <td style={{ ...styles.td, fontSize: 7, color: T.text2 }}>{iface.security?.substring(0, 25)}...</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ ...styles.detailTitle, color: T.red, marginBottom: 12 }}>🔒 SECURITY FRAMEWORK</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
          {[
            { label: "Encryption at Rest", value: securityProfile.encryptionAtRest, icon: "🔐", color: T.purple },
            { label: "Encryption in Transit", value: securityProfile.encryptionInTransit, icon: "🔒", color: T.cyan },
            { label: "IT Auth", value: securityProfile.authentication.itLayer, icon: "👤", color: T.blue },
            { label: "OT Auth", value: securityProfile.authentication.otLayer, icon: "🤖", color: T.orange },
            { label: "API Auth", value: securityProfile.authentication.apiLayer, icon: "🔑", color: T.green },
            { label: "Authorization", value: securityProfile.authorization, icon: "🛡️", color: T.teal },
            { label: "Audit Logging", value: securityProfile.auditLogging, icon: "📋", color: T.amber },
            { label: "Secrets", value: securityProfile.secretManagement, icon: "🗝️", color: T.pink },
          ].map((item, i) => (
            <div key={i} style={{ background: T.bg2, border: `1px solid ${item.color}25`, borderRadius: 6, padding: "10px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                          <span style={{ fontSize: 14 }}>{item.icon}</span>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 8, color: item.color, textTransform: "uppercase" }}>{item.label}</span>
                </div>
                <div style={{ fontSize: 8, color: T.text2, lineHeight: 1.4 }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

  );
}

// ═══════════════════════════════════════════════════════════════════════
// ENHANCED PDF REPORT GENERATOR (From V6.0)
// ═══════════════════════════════════════════════════════════════════════

function generatePDFReport(state) {
  const progress = getTotalProgress(state.dodState, state.nisState);
  const now = new Date();
  const totalComments = Object.values(state.comments || {}).reduce((sum, arr) => sum + arr.length, 0);
  const totalCustomAttrs = Object.values(state.customTriggerAttrs || {}).reduce((sum, arr) => sum + arr.length, 0);

  const styles_css = `
    <style>
      @page { size: A4; margin: 12mm 10mm; }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Inter', 'Segoe UI', Arial, sans-serif; font-size: 8px; color: #1a1a2e; line-height: 1.45; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { page-break-after: always; padding: 5px 0; }
      .page:last-child { page-break-after: auto; }
      .header { background: linear-gradient(135deg, #030912, #0c1c30); color: #fff; padding: 16px 20px; border-radius: 8px; margin-bottom: 16px; }
      .header-title { font-family: 'Orbitron', sans-serif; font-size: 18px; font-weight: 900; letter-spacing: 1.5px; background: linear-gradient(135deg, #fff, #00e5ff, #ffd600); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .header-sub { font-size: 7px; letter-spacing: 2px; text-transform: uppercase; opacity: 0.7; }
      .header-meta { display: flex; gap: 20px; font-size: 7px; opacity: 0.8; margin-top: 6px; flex-wrap: wrap; }
      .section-title { font-family: 'Orbitron', monospace; font-size: 11px; font-weight: 700; margin: 14px 0 8px; padding-bottom: 4px; border-bottom: 2px solid #e0e0e0; }
      .kpi-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 6px; margin-bottom: 14px; }
      .kpi-card { background: #f8f9fa; border-radius: 6px; padding: 10px 8px; text-align: center; border-left: 3px solid; }
      .kpi-value { font-family: 'Orbitron', monospace; font-size: 18px; font-weight: 700; }
      .kpi-label { font-size: 6px; text-transform: uppercase; letter-spacing: 0.8px; color: #888; margin-top: 2px; }
      table { width: 100%; border-collapse: collapse; font-size: 7px; margin-bottom: 10px; }
      thead th { background: #1a1a2e; color: #ddeeff; padding: 5px 4px; text-align: left; font-size: 6px; letter-spacing: 0.6px; text-transform: uppercase; }
      tbody td { padding: 4px; border-bottom: 1px solid #eee; font-size: 6.5px; vertical-align: top; }
      tbody tr:nth-child(even) { background: #f8f9fa; }
      .badge { display: inline-block; padding: 1px 6px; border-radius: 8px; font-size: 6px; font-weight: 600; }
      .badge-success { background: #d4edda; color: #155724; }
      .badge-warning { background: #fff3cd; color: #856404; }
      .badge-danger { background: #f8d7da; color: #721c24; }
      .mini-progress { display: flex; align-items: center; gap: 4px; }
      .mini-progress-bar { width: 40px; height: 3px; background: #e0e0e0; border-radius: 2px; overflow: hidden; }
      .mini-progress-fill { height: 100%; border-radius: 2px; }
      .comment-section { background: #fff8e1; border-left: 3px solid #ffb300; border-radius: 4px; padding: 6px 10px; margin: 6px 0; }
      .comment-item { padding: 3px 0; border-bottom: 1px solid #ffe082; font-size: 6.5px; }
      .comment-author { font-weight: 600; color: #f57c00; }
      .comment-text { color: #555; margin-top: 2px; }
      .custom-attr { background: #f3e5f5; border-left: 3px solid #9c27b0; border-radius: 4px; padding: 6px 10px; margin: 6px 0; }
      .footer { text-align: center; font-size: 6px; color: #aaa; margin-top: 20px; padding-top: 8px; border-top: 1px solid #eee; }
    </style>
  `;

  let html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>CLM Comprehensive Implementation Report</title>${styles_css}</head><body>`;

  // PAGE 1: COVER & EXECUTIVE SUMMARY
  html += `<div class="page">`;
  html += `<div class="header">
    <div class="header-sub">Siemens Closed-Loop Manufacturing · Implementation Tracker</div>
    <div class="header-title">CLM MISSION CONTROL</div>
    <div style="font-size:9px;margin-top:2px;">Comprehensive Implementation Report with Comments & Custom Attributes</div>
    <div class="header-meta">
      <span>📅 ${now.toLocaleDateString('en-US',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</span>
      <span>📋 40 Interfaces · ${getTotalTriggers()} Triggers · ${getTotalTasks()} Tasks</span>
      <span>💬 ${totalComments} Comments · ⚙️ ${totalCustomAttrs} Custom Attributes</span>
    </div>
  </div>`;

  html += `<div class="kpi-grid">`;
  [[T.cyan,getTotalInterfaces(),"Interfaces"],[getProgressColor(progress.pct),progress.pct+"%","Progress"],
   [T.pink,getTotalTriggers(),"Triggers"],[T.green,progress.done,"DoD Done"],
   [T.amber,totalComments,"Comments"],[T.purple,totalCustomAttrs,"Custom Attrs"]].forEach(([c,v,l])=>{
    html += `<div class="kpi-card" style="border-left-color:${c};"><div class="kpi-value" style="color:${c};">${v}</div><div class="kpi-label">${l}</div></div>`;
  });
  html += `</div>`;

  // Interface Summary Table
  html += `<div class="section-title">📋 INTERFACE SUMMARY</div>`;
  html += `<table><thead><tr><th>ID</th><th>Interface</th><th>Group</th><th>MVP</th><th>Phase</th><th>Progress</th><th>Comments</th><th>NIS</th></tr></thead><tbody>`;
  
  IFACES.forEach(iface => {
    const prog = getInterfaceProgress(iface, state.dodState, state.nisState);
    const pc = prog.pct === 100 ? '#00ff88' : prog.pct >= 50 ? '#ffb300' : '#ff4444';
    const ifaceComments = (state.comments?.[iface.id]?.length || 0);
    const triggerCommentKeys = iface.triggers?.map(t => `${iface.id}-TRG-${t.id}`) || [];
    const totalTrigComments = triggerCommentKeys.reduce((s,k)=>s+(state.comments?.[k]?.length||0),0);
    const totalComm = ifaceComments + totalTrigComments;
    
    html += `<tr><td>${iface.id}</td><td><strong>${escapeHtml(iface.name.substring(0,40))}${iface.name.length>40?'…':''}</strong></td>
      <td>${iface.grp}</td><td>MVP-${iface.mvp}</td><td>P${iface.phase}</td>
      <td><div class="mini-progress"><div class="mini-progress-bar"><div class="mini-progress-fill" style="width:${prog.pct}%;background:${pc};"></div></div><span>${prog.pct}%</span></div></td>
      <td style="text-align:center;">${totalComm>0?`💬${totalComm}`:'—'}</td>
      <td style="text-align:center;">${prog.nis?'⛔':'✓'}</td>
    </tr>`;
  });
  html += `</tbody></table>`;
  html += `</div>`;

  // DETAILED INTERFACE PAGES
  IFACES.filter(f => {
    const p = getInterfaceProgress(f, state.dodState, state.nisState);
    const hasComments = (state.comments?.[f.id]?.length || 0) > 0;
    return p.pct > 0 || hasComments;
  }).forEach(iface => {
    const prog = getInterfaceProgress(iface, state.dodState, state.nisState);
    const pc = prog.pct === 100 ? '#00ff88' : prog.pct >= 50 ? '#ffb300' : '#ff4444';
    const ifaceComments = state.comments?.[iface.id] || [];
    
    html += `<div class="page">`;
    html += `<div class="section-title">${iface.id}: ${escapeHtml(iface.name)}</div>`;
    html += `<div style="font-size:7px;color:#666;margin-bottom:8px;">${escapeHtml(iface.from)} → ${escapeHtml(iface.to)} | ${iface.grp} | MVP-${iface.mvp} | Phase ${iface.phase} | <span style="color:${pc};">${prog.pct}%</span></div>`;
    html += `<div style="font-size:7px;color:#555;margin-bottom:10px;">${escapeHtml(iface.about||'')}</div>`;
    
    if (ifaceComments.length > 0) {
      html += `<div class="comment-section"><strong>💬 Interface Comments (${ifaceComments.length}):</strong>`;
      ifaceComments.forEach(c => {
        html += `<div class="comment-item"><span class="comment-author">${escapeHtml(c.author)}</span> · ${new Date(c.timestamp).toLocaleString()}<div class="comment-text">${escapeHtml(c.text)}</div></div>`;
      });
      html += `</div>`;
    }
    
    if (iface.triggers) {
      iface.triggers.forEach(trigger => {
        const triggerCommentKey = `${iface.id}-TRG-${trigger.id}`;
        const triggerComments = state.comments?.[triggerCommentKey] || [];
        const customAttrs = state.customTriggerAttrs?.[trigger.id] || [];
        
        html += `<div style="margin:8px 0;padding:6px;border:1px solid #e0e0e0;border-radius:4px;">`;
        html += `<strong style="font-size:8px;">⚡ ${escapeHtml(trigger.name)}</strong> <span class="badge badge-warning">${escapeHtml(trigger.type||'N/A')}</span>`;
        if (trigger.latency) html += ` <span class="badge badge-info">⏱️ ${escapeHtml(trigger.latency)}</span>`;
        html += `<div style="font-size:6.5px;color:#666;">${escapeHtml(trigger.description||'')}</div>`;
        
        if (triggerComments.length > 0) {
          html += `<div class="comment-section"><strong>💬 Trigger Comments:</strong>`;
          triggerComments.forEach(c => {
            html += `<div class="comment-item"><span class="comment-author">${escapeHtml(c.author)}</span> · ${new Date(c.timestamp).toLocaleString()}<div class="comment-text">${escapeHtml(c.text)}</div></div>`;
          });
          html += `</div>`;
        }
        
        if (customAttrs.length > 0) {
          html += `<div class="custom-attr"><strong>⚙️ Custom Attributes:</strong>`;
          customAttrs.forEach(a => {
            html += `<div style="font-size:6.5px;">• <strong>${escapeHtml(a.key)}:</strong> ${escapeHtml(a.value)}</div>`;
          });
          html += `</div>`;
        }
        html += `</div>`;
      });
    }
    
    if (iface.tasks) {
      iface.tasks.forEach(task => {
        const completed = task.dod.filter((_,di)=>state.dodState[`${task.id}-${di}`]).length;
        html += `<div style="margin:4px 0;font-size:7px;"><strong>✅ ${escapeHtml(task.name)}</strong> (${completed}/${task.dod.length} DoD) · ${task.hours}h · ${CX_META[task.cx]?.l||'N/A'} complexity</div>`;
      });
    }
    
    html += `</div>`;
  });

  // FINAL PAGE
  html += `<div class="page">`;
  html += `<div class="section-title">📦 EXPORT METADATA</div>`;
  html += `<div style="font-size:7px;color:#555;">
    <strong>Version:</strong> 9.0 · <strong>Date:</strong> ${now.toLocaleString()}<br/>
    <strong>Total Comments:</strong> ${totalComments} · <strong>Custom Attributes:</strong> ${totalCustomAttrs}<br/>
    <strong>Includes:</strong> All DoD states, NIS flags, comments, custom trigger attributes, filters, expanded views
  </div>`;
  html += `<div class="footer"><strong>CLM MISSION CONTROL</strong> · Siemens · Confidential · Generated: ${now.toLocaleString()}</div>`;
  html += `</div>`;

  html += `</body></html>`;

  const printWindow = window.open('', '_blank', 'width=1200,height=800');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 600);
  } else {
    downloadFile(html, `CLM_Report_${now.toISOString().slice(0,10)}.html`, "text/html");
  }
}

// ═══════════════════════════════════════════════════════════════════════
// EXPORT TAB - ENHANCED WITH COMPLETE STATE PRESERVATION
// ═══════════════════════════════════════════════════════════════════════

function ExportTab({ state, dispatch }) {
  const progress = getTotalProgress(state.dodState, state.nisState);
  const nisCount = Object.values(state.nisState).filter(Boolean).length;
  const totalTriggers = getTotalTriggers();
  const totalTasks = getTotalTasks();
  const totalDoD = getTotalDoDCriteria();
  const totalComments = Object.values(state.comments || {}).reduce((sum, arr) => sum + arr.length, 0);
  const totalCustomAttrs = Object.values(state.customTriggerAttrs || {}).reduce((sum, arr) => sum + arr.length, 0);
  const activeFiltersCount = Object.values(state.filters || {}).reduce((sum, set) => sum + (set?.size || 0), 0);
  const [importStatus, setImportStatus] = useState(null);
  const [showExportPreview, setShowExportPreview] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [exportFiltered, setExportFiltered] = useState(false);
  const fileInputRef = useRef(null);

  const buildExportData = useCallback(() => {
    const sourceList = exportFiltered ? filterInterfaces(state) : IFACES;
    const interfaceDetails = sourceList.map(iface => {
      const prog = getInterfaceProgress(iface, state.dodState, state.nisState);
      const taskDetails = (iface.tasks || []).map(task => ({
        id: task.id,
        name: task.name,
        type: task.type,
        hours: task.hours,
        complexity: task.complexity,
        dodItems: (task.dod || []).map((dodText, idx) => ({
          index: idx,
          text: dodText,
          completed: !!state.dodState[`${task.id}-${idx}`],
          completedAt: state.dodTimestamps?.[`${task.id}-${idx}`] || null,
        })),
        progress: {
          completed: (task.dod || []).filter((_, idx) => state.dodState[`${task.id}-${idx}`]).length,
          total: (task.dod || []).length,
        },
      }));
      
      return {
        id: iface.id,
        seq: iface.seq,
        name: iface.name,
        grp: iface.grp,
        mvp: iface.mvp,
        phase: iface.phase,
        complexity: iface.complexity,
        effort: iface.effort,
        risk: iface.risk,
        from: iface.from,
        to: iface.to,
        bo: iface.bo,
        tasks: taskDetails,
        triggers: iface.triggers?.length || 0,
        progress: prog,
        isNIS: !!state.nisState[iface.id],
        commentCount: (state.comments?.[iface.id]?.length || 0),
      };
    });

    return {
      metadata: {
        version: "10.0",
        exportDate: new Date().toISOString(),
        applicationName: "CLM MISSION CONTROL",
        totalInterfaces: getTotalInterfaces(),
        totalTriggers,
        totalTasks,
        totalDoD,
        overallProgress: progress,
        nisCount,
        totalComments,
        totalCustomAttrs,
        activeFiltersCount,
        // ─── NEW v10 counts ──────────────────────────────────────────
        benefitsChecked: Object.values(state.benefitsState || {}).filter(Boolean).length,
        benefitsTotal:   Object.keys(state.benefitsState || {}).length,
        agentsConfigured: Object.keys(state.agentConfig || {}).length,
      },
      dodState: { ...state.dodState },
      dodTimestamps: { ...state.dodTimestamps },
      nisState: { ...state.nisState },
      comments: state.comments || {},
      customTriggerAttrs: state.customTriggerAttrs || {},
      filters: Object.fromEntries(Object.entries(state.filters || {}).map(([k, v]) => [k, Array.from(v || [])])),
      expandedInterfaces: Array.from(state.expanded || []),
      activeTab: state.activeTab,
      sortBy: state.sortBy,
      // ─── NEW v10 state slices (Benefits + Agentic AI) ────────────────
      benefitsState: { ...(state.benefitsState || {}) },
      benefitOwners: { ...(state.benefitOwners || {}) },
      benefitNotes:  { ...(state.benefitNotes  || {}) },
      agentConfig:   { ...(state.agentConfig   || {}) },
      interfaceDetails,
    };
  }, [state, progress, totalTriggers, totalTasks, totalDoD, nisCount, totalComments, totalCustomAttrs, activeFiltersCount, exportFiltered]);

  const handleExportJSON = (includeFullDetails = true) => {
    const data = buildExportData();
    if (!includeFullDetails) {
      const lightweight = {
        metadata: data.metadata,
        dodState: data.dodState,
        dodTimestamps: data.dodTimestamps,
        nisState: data.nisState,
        comments: data.comments,
        customTriggerAttrs: data.customTriggerAttrs,
        filters: data.filters,
        expandedInterfaces: data.expandedInterfaces,
        activeTab: data.activeTab,
        sortBy: data.sortBy,
        // ─── NEW: include the v10 state slices in lightweight too ───
        benefitsState: data.benefitsState,
        benefitOwners: data.benefitOwners,
        benefitNotes:  data.benefitNotes,
        agentConfig:   data.agentConfig,
      };
      downloadFile(JSON.stringify(lightweight, null, 2), `clm_state_${new Date().toISOString().slice(0,10)}.json`, "application/json");
    } else {
      downloadFile(JSON.stringify(data, null, 2), `clm_complete_export_${new Date().toISOString().slice(0,10)}.json`, "application/json");
    }
    setShowExportPreview(false);
    setExportData(null);
  };

  const handleExportCSV = () => {
    const rows = [["Interface ID", "Interface Name", "Group", "MVP", "Phase", "Complexity", "Effort (days)", "Risk", "Progress (%)", "DoD Done", "DoD Total", "Triggers", "Tasks", "Not In Scope", "From", "To", "Business Object", "Comments"]];
    
    IFACES.forEach(iface => {
      const prog = getInterfaceProgress(iface, state.dodState, state.nisState);
      const commentCount = (state.comments?.[iface.id]?.length || 0);
      rows.push([
        iface.id, iface.name, iface.grp, `MVP-${iface.mvp}`, iface.phase,
        CX_META[iface.complexity]?.l || iface.complexity, iface.effort, RISK_META[iface.risk]?.l || iface.risk,
        prog.pct, prog.done, prog.total, iface.triggers?.length || 0, iface.tasks?.length || 0,
        state.nisState[iface.id] ? "Yes" : "No", iface.from, iface.to, iface.bo || "", commentCount
      ]);
    });
    
    const csvContent = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
    downloadFile(csvContent, `clm_interfaces_report_${new Date().toISOString().slice(0,10)}.csv`, "text/csv");
  };

  const handleExportDoDDetail = () => {
    const rows = [["Interface ID", "Interface Name", "Group", "MVP", "Phase", "Task ID", "Task Name", "Task Type", "DoD #", "Definition of Done", "Status", "Completed At"]];
    
    IFACES.forEach(iface => {
      (iface.tasks || []).forEach(task => {
        (task.dod || []).forEach((dod, idx) => {
          const key = `${task.id}-${idx}`;
          const isDone = !!state.dodState[key];
          rows.push([
            iface.id, iface.name, iface.grp, `MVP-${iface.mvp}`, iface.phase,
            task.id, task.name, task.type, idx + 1, dod, isDone ? "Completed" : "Pending",
            state.dodTimestamps?.[key] || (isDone ? "Exported" : "")
          ]);
        });
      });
    });
    
    const csvContent = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
    downloadFile(csvContent, `clm_dod_detail_${new Date().toISOString().slice(0,10)}.csv`, "text/csv");
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportStatus({ type: 'loading', message: 'Reading file...' });
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!data.dodState && !data.nisState) throw new Error('Invalid export file: No state data found');
        
        dispatch({ type: "IMPORT_STATE", payload: data });
        const commentCount = Object.values(data.comments||{}).reduce((s,a)=>s+a.length,0);
        const attrCount = Object.values(data.customTriggerAttrs||{}).reduce((s,a)=>s+a.length,0);
        setImportStatus({ type: 'success', message: `✅ Imported! DoD: ${Object.values(data.dodState||{}).filter(Boolean).length} items, 💬 ${commentCount} comments, ⚙️ ${attrCount} attrs` });
        setTimeout(() => setImportStatus(null), 5000);
      } catch (err) {
        setImportStatus({ type: 'error', message: `❌ Import failed: ${err.message}` });
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsText(file);
  };

  return (
    <div style={{ padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 14, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>📤 Export & Reports</div>
        <div style={{ fontSize: 10, color: T.text2 }}>Export/import complete state including comments and custom attributes</div>
      </div>

      {importStatus && (
        <div style={{ padding: "10px 14px", marginBottom: 16, borderRadius: 6,
          background: importStatus.type === 'success' ? `${T.success}10` : importStatus.type === 'error' ? `${T.red}10` : `${T.cyan}10`,
          border: `1px solid ${importStatus.type === 'success' ? T.success : importStatus.type === 'error' ? T.red : T.cyan}30` }}>
          <span style={{ fontSize: 10, color: importStatus.type === 'error' ? T.red : T.text }}>{importStatus.message}</span>
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
        {[[T.cyan,getTotalInterfaces(),"Interfaces"],[T.pink,getTotalTriggers(),"Triggers"],
          [T.green,progress.done,"DoD Done"],[T.amber,totalComments,"Comments"],
          [T.purple,totalCustomAttrs,"Custom Attrs"],[T.red,nisCount,"NIS"]].map(([c,v,l])=>(
          <div key={l} style={{...styles.kpiCard,minWidth:75}}>
            <div style={{...styles.kpiValue,color:c,fontSize:18}}>{v}</div>
            <div style={styles.kpiLabel}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, padding: "16px 20px", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontWeight: 600, fontSize: 11 }}>Overall Progress</span>
          <span style={{ fontFamily: FONTS.header, fontSize: 13, fontWeight: 700, color: getProgressColor(progress.pct) }}>{progress.pct}%</span>
        </div>
        <div style={{ height: 8, background: T.bg3, borderRadius: 4, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${progress.pct}%`, background: "linear-gradient(90deg, #00e5ff, #00ff88)", borderRadius: 4 }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 9, color: T.text2 }}>
          <span>Completed: {progress.done}</span><span>Remaining: {progress.total - progress.done}</span><span>Total: {progress.total}</span>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <div style={{ ...styles.detailTitle, color: T.blue, marginBottom: 12 }}>📤 EXPORT OPTIONS</div>
        <div style={{ marginBottom: 12, padding: "10px 14px", background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 6 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 10, color: T.text, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={exportFiltered}
              onChange={e => setExportFiltered(e.target.checked)}
              style={{ cursor: "pointer" }} />
            <span>
              {exportFiltered
                ? <>Exporting <strong style={{ color: T.cyan }}>{filterInterfaces(state).length}</strong> filtered interfaces</>
                : <>Exporting all <strong style={{ color: T.cyan }}>{IFACES.length}</strong> interfaces (toggle to honor active filters)</>}
            </span>
          </label>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12, marginBottom: 16 }}>
          
          {/* PDF Report - Featured */}
          <div style={{ background: `linear-gradient(135deg, ${T.red}10, ${T.amber}08)`, border: `2px solid ${T.red}40`, borderRadius: 8, padding: "14px 16px", gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 24 }}>📕</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: T.text, marginBottom: 2 }}>Comprehensive PDF Report</div>
                <div style={{ fontSize: 9, color: T.text2 }}>Professional multi-page report with all statistics and analysis</div>
              </div>
              <button onClick={() => generatePDFReport(state)} style={{ background: `linear-gradient(135deg, ${T.red}, ${T.orange})`, border: "none", color: "#fff", padding: "10px 24px", borderRadius: 6, cursor: "pointer", fontFamily: FONTS.mono, fontSize: 11, fontWeight: 600 }}>
                📥 Generate PDF Report
              </button>
            </div>
          </div>

          {/* Complete JSON Export */}
          <div style={{ background: T.bg2, border: `1px solid ${T.green}30`, borderRadius: 8, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 18 }}>📄</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 11, color: T.green }}>Complete State Export (JSON)</div>
                <div style={{ fontSize: 9, color: T.text2 }}>Full backup with all states preserved</div>
              </div>
            </div>
            <button onClick={() => handleExportJSON(true)} style={{ background: `${T.green}15`, border: `1px solid ${T.green}40`, color: T.green, padding: "8px 16px", borderRadius: 6, cursor: "pointer", width: "100%" }}>
              📥 Download Complete Export
            </button>
          </div>

          {/* State-Only Export */}
          <div style={{ background: T.bg2, border: `1px solid ${T.cyan}30`, borderRadius: 8, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 18 }}>💾</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 11, color: T.cyan }}>State-Only Export (JSON)</div>
                <div style={{ fontSize: 9, color: T.text2 }}>Compact state preservation</div>
              </div>
            </div>
            <button onClick={() => handleExportJSON(false)} style={{ background: `${T.cyan}15`, border: `1px solid ${T.cyan}40`, color: T.cyan, padding: "8px 16px", borderRadius: 6, cursor: "pointer", width: "100%" }}>
              📥 Download State Export
            </button>
          </div>

          {/* CSV Interface Summary */}
          <div style={{ background: T.bg2, border: `1px solid ${T.blue}30`, borderRadius: 8, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 18 }}>📊</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 11, color: T.blue }}>Interface Summary (CSV)</div>
                <div style={{ fontSize: 9, color: T.text2 }}>Spreadsheet-ready interface report</div>
              </div>
            </div>
            <button onClick={handleExportCSV} style={{ background: `${T.blue}15`, border: `1px solid ${T.blue}40`, color: T.blue, padding: "8px 16px", borderRadius: 6, cursor: "pointer", width: "100%" }}>
              📥 Download CSV Report
            </button>
          </div>

          {/* DoD Detail Export */}
          <div style={{ background: T.bg2, border: `1px solid ${T.purple}30`, borderRadius: 8, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 18 }}>📋</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 11, color: T.purple }}>DoD Detail Report (CSV)</div>
                <div style={{ fontSize: 9, color: T.text2 }}>Every DoD item with status</div>
              </div>
            </div>
            <button onClick={handleExportDoDDetail} style={{ background: `${T.purple}15`, border: `1px solid ${T.purple}40`, color: T.purple, padding: "8px 16px", borderRadius: 6, cursor: "pointer", width: "100%" }}>
              📥 Download DoD Detail
            </button>
          </div>
        </div>
      </div>

      {/* Import Section */}
      <div style={{ background: `${T.cyan}08`, border: `1px solid ${T.cyan}30`, borderRadius: 8, padding: "16px 20px", marginBottom: 20 }}>
        <div style={{ ...styles.detailTitle, color: T.cyan, marginBottom: 8 }}>⬆ IMPORT STATE</div>
        <div style={{ fontSize: 10, color: T.text2, marginBottom: 12 }}>
          Restore a previously exported JSON file to continue tracking exactly where you left off.
        </div>
        <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} style={{ background: T.bg2, border: `1px solid ${T.border}`, color: T.text, padding: "10px 14px", borderRadius: 6, fontSize: 10, width: "100%", cursor: "pointer" }} />
      </div>

      {/* Current State Summary */}
      <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, padding: "16px 20px" }}>
        <div style={{ ...styles.detailTitle, color: T.steel, marginBottom: 8 }}>📊 CURRENT STATE SUMMARY</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
          {[
            { label: "DoD Completed", value: `${progress.done}/${progress.total}`, color: T.success },
            { label: "Interfaces NIS", value: nisCount, color: T.red },
            { label: "Expanded Views", value: state.expanded?.size || 0, color: T.cyan },
            { label: "Active Filters", value: activeFiltersCount, color: T.purple },
            { label: "Total Comments", value: totalComments, color: T.amber },
            { label: "Custom Attributes", value: totalCustomAttrs, color: T.pink },
          ].map((item, i) => (
            <div key={i} style={{ background: `${item.color}08`, border: `1px solid ${item.color}20`, borderRadius: 6, padding: "8px 12px", display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 9, color: T.text2 }}>{item.label}</span>
              <span style={{ fontSize: 10, fontWeight: 600, color: item.color, fontFamily: FONTS.mono }}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// REDUCER & INITIAL STATE
// ═══════════════════════════════════════════════════════════════════════

const STORAGE_KEY = "clm_mission_control_v10";
const STORAGE_KEY_V9 = "clm_mission_control_v9";

// One-time migration: if there's no v10 state but v9 state exists, copy it.
function migrateFromV9IfNeeded() {
  try {
    const v10 = localStorage.getItem(STORAGE_KEY);
    if (v10 != null) return; // already migrated or fresh v10
    const v9 = localStorage.getItem(STORAGE_KEY_V9);
    if (v9 == null) return; // no v9 either, fresh install
    // Copy v9 into v10 verbatim — v10 is a superset of v9 keys
    localStorage.setItem(STORAGE_KEY, v9);
    // Leave v9 in place so an explicit user re-install can roll back.
    // eslint-disable-next-line no-console
    console.info("[CLM] Migrated state from v9 → v10 keyspace.");
  } catch {
    /* localStorage unavailable; nothing to migrate */
  }
}
migrateFromV9IfNeeded();

function loadState() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return {
      activeTab: saved.activeTab || "table",
      dodState: saved.dodState || {},
      dodTimestamps: saved.dodTimestamps || {},
      nisState: saved.nisState || {},
      expanded: new Set(saved.expandedInterfaces || []),
      filters: Object.fromEntries(Object.entries(saved.filters || {}).map(([k,v]) => [k, new Set(Array.isArray(v)?v:[])])),
      sortBy: saved.sortBy || "seq",
      groupBy: saved.groupBy || "none",
      search: saved.search || "",
      comments: saved.comments || {},
      customTriggerAttrs: saved.customTriggerAttrs || {},
      selectedInterface: saved.selectedInterface || null,
      // ─── NEW: state for the 8 advanced tabs ─────────────────────────
      benefitsState: saved.benefitsState || {},          // { [id]: boolean }
      benefitOwners: saved.benefitOwners || {},          // { [id]: string }
      benefitNotes: saved.benefitNotes || {},            // { [id]: string }
      agentConfig: saved.agentConfig || {},              // { [agentId]: { level, enabled, note } }
      // ─── NEW: maturity/complexity profile ───────────────────────────
      maturity: saved.maturity ?? 3,                     // L0–L5 (default L3)
      complexity: saved.complexity ?? 4,                 // C1–C5 (default C4)
    };
  } catch {
    return {
      activeTab: "table",
      dodState: {},
      dodTimestamps: {},
      nisState: {},
      expanded: new Set(),
      filters: {},
      sortBy: "seq",
      groupBy: "none",
      search: "",
      comments: {},
      customTriggerAttrs: {},
      selectedInterface: null,
      benefitsState: {},
      benefitOwners: {},
      benefitNotes: {},
      agentConfig: {},
      maturity: 3,
      complexity: 4,
    };
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      activeTab: state.activeTab,
      dodState: state.dodState,
      dodTimestamps: state.dodTimestamps,
      nisState: state.nisState,
      expandedInterfaces: Array.from(state.expanded),
      filters: Object.fromEntries(Object.entries(state.filters).map(([k,v]) => [k, Array.from(v)])),
      sortBy: state.sortBy,
      groupBy: state.groupBy,
      search: state.search,
      comments: state.comments,
      customTriggerAttrs: state.customTriggerAttrs,
      selectedInterface: state.selectedInterface,
      // NEW: persist the 8 advanced tab state slices
      benefitsState: state.benefitsState || {},
      benefitOwners: state.benefitOwners || {},
      benefitNotes: state.benefitNotes || {},
      agentConfig: state.agentConfig || {},
      maturity: state.maturity ?? 3,
      complexity: state.complexity ?? 4,
    }));
  } catch {}
}

function appReducer(state, action) {
  let newState;
  switch (action.type) {
    case "TOGGLE_DOD": {
      const key = `${action.taskId}-${action.dodIdx}`;
      const newValue = !state.dodState[key];
      newState = {
        ...state,
        dodState: { ...state.dodState, [key]: newValue },
        dodTimestamps: { ...state.dodTimestamps, [key]: newValue ? new Date().toISOString() : null }
      };
      break;
    }
    case "TOGGLE_NIS":
      newState = { ...state, nisState: { ...state.nisState, [action.ifaceId]: !state.nisState[action.ifaceId] } };
      break;
    case "TOGGLE_EXPAND": {
      const expanded = new Set(state.expanded);
      if (expanded.has(action.id)) expanded.delete(action.id); else expanded.add(action.id);
      newState = { ...state, expanded };
      break;
    }
    case "SET_TAB":
      newState = { ...state, activeTab: action.tab };
      break;
    case "SET_PROFILE": {
      // Setting maturity/complexity reconfigures the whole app. If the
      // currently-open tab is no longer visible under the new profile,
      // fall back to the config tab so the user is never stranded.
      const prof = resolveProfile(action.maturity, action.complexity);
      const stillVisible = prof.visibleTabs.includes(state.activeTab);
      newState = {
        ...state,
        maturity: action.maturity,
        complexity: action.complexity,
        groupScope: prof.groupsInScope,
        activeTab: stillVisible ? state.activeTab : "config",
      };
      break;
    }
    case "SET_FILTER": {
      const filters = { ...state.filters };
      if (!filters[action.key]) filters[action.key] = new Set();
      const values = new Set(filters[action.key]);
      if (values.has(action.value)) values.delete(action.value); else values.add(action.value);
      filters[action.key] = values;
      newState = { ...state, filters };
      break;
    }
    case "CLEAR_FILTER": {
      const filters = { ...state.filters };
      delete filters[action.key];
      newState = { ...state, filters };
      break;
    }
    case "CLEAR_ALL_FILTERS":
      newState = { ...state, filters: {} };
      break;
    case "SET_SORT":
      newState = { ...state, sortBy: action.value };
      break;
    case "SET_SEARCH":
      newState = { ...state, search: action.value };
      break;
    case "ADD_COMMENT": {
      const comments = { ...state.comments };
      if (!comments[action.targetId]) comments[action.targetId] = [];
      comments[action.targetId] = [...comments[action.targetId], action.comment];
      newState = { ...state, comments };
      break;
    }
    case "DELETE_COMMENT": {
      const comments = { ...state.comments };
      if (comments[action.targetId]) {
        comments[action.targetId] = comments[action.targetId].filter((_, i) => i !== action.commentIndex);
        if (comments[action.targetId].length === 0) delete comments[action.targetId];
      }
      newState = { ...state, comments: { ...comments } };
      break;
    }
    case "ADD_TRIGGER_ATTRIBUTE": {
      const customTriggerAttrs = { ...state.customTriggerAttrs };
      if (!customTriggerAttrs[action.triggerId]) customTriggerAttrs[action.triggerId] = [];
      customTriggerAttrs[action.triggerId] = [...customTriggerAttrs[action.triggerId], action.attr];
      newState = { ...state, customTriggerAttrs };
      break;
    }
    case "DELETE_TRIGGER_ATTRIBUTE": {
      const customTriggerAttrs = { ...state.customTriggerAttrs };
      if (customTriggerAttrs[action.triggerId]) {
        customTriggerAttrs[action.triggerId] = customTriggerAttrs[action.triggerId].filter((_, i) => i !== action.attrIndex);
        if (customTriggerAttrs[action.triggerId].length === 0) delete customTriggerAttrs[action.triggerId];
      }
      newState = { ...state, customTriggerAttrs: { ...customTriggerAttrs } };
      break;
    }
    case "SET_SELECTED_INTERFACE":
      newState = { ...state, selectedInterface: action.interfaceId };
      break;

    // ─── NEW: Benefits checklist actions ─────────────────────────────
    case "TOGGLE_BENEFIT": {
      const benefitsState = { ...state.benefitsState };
      benefitsState[action.id] = !benefitsState[action.id];
      newState = { ...state, benefitsState };
      break;
    }
    case "SET_BENEFIT_OWNER": {
      const benefitOwners = { ...state.benefitOwners };
      if (action.owner) benefitOwners[action.id] = action.owner;
      else delete benefitOwners[action.id];
      newState = { ...state, benefitOwners };
      break;
    }
    case "SET_BENEFIT_NOTE": {
      const benefitNotes = { ...state.benefitNotes };
      if (action.note) benefitNotes[action.id] = action.note;
      else delete benefitNotes[action.id];
      newState = { ...state, benefitNotes };
      break;
    }

    // ─── NEW: Agentic AI config actions ──────────────────────────────
    case "SET_AGENT_LEVEL": {
      const agentConfig = { ...state.agentConfig };
      agentConfig[action.agentId] = { ...(agentConfig[action.agentId] || {}), level: action.level };
      newState = { ...state, agentConfig };
      break;
    }
    case "TOGGLE_AGENT_ENABLED": {
      const agentConfig = { ...state.agentConfig };
      const cur = agentConfig[action.agentId] || {};
      const wasEnabled = cur.enabled !== false; // default true
      agentConfig[action.agentId] = { ...cur, enabled: !wasEnabled };
      newState = { ...state, agentConfig };
      break;
    }
    case "SET_AGENT_NOTE": {
      const agentConfig = { ...state.agentConfig };
      agentConfig[action.agentId] = { ...(agentConfig[action.agentId] || {}), note: action.note };
      newState = { ...state, agentConfig };
      break;
    }

    case "IMPORT_STATE": {
      const p = action.payload;
      newState = {
        ...state,
        dodState: p.dodState || {},
        dodTimestamps: p.dodTimestamps || {},
        nisState: p.nisState || {},
        expanded: new Set(p.expandedInterfaces || []),
        filters: Object.fromEntries(Object.entries(p.filters || {}).map(([k,v]) => [k, new Set(Array.isArray(v)?v:[])])),
        comments: p.comments || {},
        customTriggerAttrs: p.customTriggerAttrs || {},
        activeTab: p.activeTab || state.activeTab,
        sortBy: p.sortBy || state.sortBy,
        selectedInterface: p.selectedInterface || null,
        // NEW: import 8-advanced-tab state slices too
        benefitsState: p.benefitsState || state.benefitsState || {},
        benefitOwners: p.benefitOwners || state.benefitOwners || {},
        benefitNotes:  p.benefitNotes  || state.benefitNotes  || {},
        agentConfig:   p.agentConfig   || state.agentConfig   || {},
      };
      break;
    }
    default:
      newState = state;
  }
  // saveState() removed from reducer — useEffect in App handles persistence.
  // Reducers must be pure (no side effects) per React rules; the previous
  // call here also caused double-writes to localStorage.
  return newState;
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN APP COMPONENT (V10.0)
// ═══════════════════════════════════════════════════════════════════════

export default function App() {
  const [state, dispatch] = useReducer(appReducer, null, loadState);
  const [showFilters, setShowFilters] = useState(true);
  const progress = getTotalProgress(state.dodState, state.nisState);
  
  const totalComments = useMemo(() => 
    Object.values(state.comments || {}).reduce((sum, arr) => sum + arr.length, 0)
  , [state.comments]);
  
  const totalCustomAttrs = useMemo(() => 
    Object.values(state.customTriggerAttrs || {}).reduce((sum, arr) => sum + arr.length, 0)
  , [state.customTriggerAttrs]);
  
  useEffect(() => {
    saveState(state);
  }, [state]);
  
  const handleInterfaceSelect = useCallback((interfaceId) => {
    dispatch({ type: "SET_SELECTED_INTERFACE", interfaceId });
    dispatch({ type: "SET_TAB", tab: "table" });
    if (!state.expanded.has(interfaceId)) {
      dispatch({ type: "TOGGLE_EXPAND", id: interfaceId });
    }
  }, [state.expanded]);

  // ─── NEW: cross-tab navigation API ──────────────────────────────────
  // Lets any child tab call nav.goToInterface(id) etc. to jump elsewhere.
  const navApi = useMemo(() => ({
    currentTab: state.activeTab,
    goToTab: (tabId) => dispatch({ type: "SET_TAB", tab: tabId }),
    goToInterface: (id) => {
      dispatch({ type: "SET_SELECTED_INTERFACE", interfaceId: id });
      dispatch({ type: "SET_TAB", tab: "lld" });
    },
    goToAgent: (_agentId) => {
      // No per-agent persistence yet; just open the Agentic AI tab.
      dispatch({ type: "SET_TAB", tab: "agentic" });
    },
    goToBenefit: (_benefitId) => {
      dispatch({ type: "SET_TAB", tab: "benefits" });
    },
    goToFlow: (_flowId) => {
      dispatch({ type: "SET_TAB", tab: "esb" });
    },
    goToGate: (_gateNum) => {
      dispatch({ type: "SET_TAB", tab: "agentic" });
    },
  }), [state.activeTab, dispatch]);
  
  // ─── Maturity/complexity profile drives the whole UI ───────────────
  const profile = useMemo(
    () => resolveProfile(state.maturity ?? 3, state.complexity ?? 4),
    [state.maturity, state.complexity]
  );

  const ALL_TABS = [
    { id: "clmstudio", label: "🎬 CLM Studio" },
    { id: "config",    label: "⚙️ Maturity Setup" },
    { id: "table",     label: "📋 Tracker" },
    { id: "kanban",    label: "📌 Kanban" },
    { id: "risk",      label: "⚠️ Risk" },
    { id: "timeline",  label: "📅 Timeline" },
    { id: "protocols", label: "🔌 Protocols" },
    { id: "visual",    label: "🏗️ Architecture" },
    { id: "e2e",       label: "🔗 E2E Gaps" },
    { id: "master",    label: "🗺️ Master Arch" },
    { id: "agentic",   label: "🤖 Agentic AI" },
    { id: "benefits",  label: "🎯 Benefits (76)" },
    { id: "esb",       label: "🚌 ESB" },
    { id: "dfd",       label: "🔄 DFD" },
    { id: "contracts", label: "📋 Contracts" },
    { id: "lld",       label: "📐 LLD" },
    { id: "intel",     label: "🧠 Intelligence" },
    { id: "plan",      label: "🏗 Implementation" },
    { id: "taxonomy",  label: "🗂 Taxonomy (107)" },
    { id: "b2mml",     label: "🏛 B2MML Federation" },
    { id: "syncGuide", label: "👥 Sync Guide" },
    { id: "export",    label: "📤 Export" },
  ];
  // Only show tabs the current maturity unlocks (config + core always present).
  // CLM Studio is always available — it's the cross-cutting visual command surface,
  // so we force-include it even if a maturity profile wouldn't list it.
  const tabs = ALL_TABS.filter(t => t.id === "clmstudio" || profile.visibleTabs.includes(t.id));

  // Inject the active group-scope into the state object passed to every tab,
  // so filterSystem hides out-of-scope interface groups app-wide. This keeps
  // scope in sync with the profile even on first load (before any SET_PROFILE).
  const scopedState = useMemo(
    () => ({ ...state, groupScope: profile.groupsInScope }),
    [state, profile.groupsInScope]
  );
  
  return (
    <NavigationProvider value={navApi}>
    <div style={styles.wrap}>
      {/* Header */}
      <div style={styles.hdr}>
        <div>
          <div style={{ fontFamily: FONTS.mono, fontSize: 8, letterSpacing: 2, color: T.cyan, opacity: 0.7, marginBottom: 4 }}>
            SIEMENS CLOSED-LOOP MANUFACTURING · DESIGN & IMPLEMENTATION PLATFORM · V10.0
          </div>
          <div style={styles.h1}>CLM MISSION CONTROL</div>
          {/* Active maturity/complexity profile badge */}
          <div
            onClick={() => dispatch({ type: "SET_TAB", tab: "config" })}
            title="Click to change maturity & complexity"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8, marginTop: 6,
              padding: "4px 10px", borderRadius: 14, cursor: "pointer",
              background: `${profile.accent}15`, border: `1px solid ${profile.accent}50`,
            }}>
            <span style={{ fontSize: 9, fontFamily: FONTS.mono, fontWeight: 700, color: profile.accent }}>
              {profile.bandMeta?.icon} {profile.maturityMeta.key}·{profile.complexityMeta.key}
            </span>
            <span style={{ fontSize: 9, color: T.text2 }}>
              #{profile.architecture.n} {profile.architecture.name}
            </span>
            <span style={{ fontSize: 8, color: T.text3 }}>⚙ change</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          {[[T.cyan,getTotalInterfaces(),"INTERFACES"],[T.pink,getTotalTriggers(),"TRIGGERS"],
            [T.green,`${progress.done}/${progress.total}`,"DoD"],[getProgressColor(progress.pct),`${progress.pct}%`,"PROGRESS"],
            [T.amber,totalComments,"COMMENTS"],[T.purple,totalCustomAttrs,"ATTRS"]].map(([c,v,l])=>(
            <div key={l} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: c }}>{v}</div>
              <div style={{ fontSize: 7, color: T.text2, letterSpacing: 1 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Control Bar */}
      <div style={styles.ctrl}>
        {tabs.map(tab => (
          <div key={tab.id} onClick={() => dispatch({ type: "SET_TAB", tab: tab.id })} style={styles.tab(state.activeTab === tab.id)}>
            {tab.label}
          </div>
        ))}
        <div style={{ width: 1, height: 24, background: T.border, margin: "0 4px" }} />
        <button onClick={() => setShowFilters(!showFilters)}
          style={{ ...styles.sel, display: "flex", alignItems: "center", gap: 6, background: showFilters ? `${T.gold}15` : "transparent", borderColor: showFilters ? T.gold : T.border, color: showFilters ? T.gold : T.text2 }}>
          <span>🔍</span> {showFilters ? "Hide Filters" : "Show Filters"}
        </button>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, minWidth: 200 }}>
          <div style={{ flex: 1, height: 4, background: T.bg3, borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress.pct}%`, background: "linear-gradient(90deg, #00e5ff, #00ff88)", borderRadius: 2 }} />
          </div>
          <span style={{ fontFamily: FONTS.header, fontSize: 11, fontWeight: 700, color: T.cyan }}>{progress.pct}%</span>
        </div>
      </div>
      
      {/* Main Content */}
      <div style={{ display: "flex", height: "calc(100vh - 108px)", overflow: "hidden" }}>
        {showFilters && ["table", "kanban", "risk", "timeline", "protocols", "export", "lld", "contracts", "intel", "plan"].includes(state.activeTab) && (
          <FilterPanel state={scopedState} dispatch={dispatch} />
        )}
        <div style={{ flex: 1, overflowY: state.activeTab === "clmstudio" ? "hidden" : "auto", overflowX: "hidden" }}>
          {state.activeTab === "clmstudio" && <ErrorBoundary tabName="CLM Studio"><CLMStudio embedded nav={navApi} /></ErrorBoundary>}
          {state.activeTab === "config"    && <ErrorBoundary tabName="Maturity Setup"> <MaturityConfigTab state={state} dispatch={dispatch} /></ErrorBoundary>}
          {state.activeTab === "table"     && <ErrorBoundary tabName="Tracker">    <TableTab state={scopedState} dispatch={dispatch} selectedInterface={state.selectedInterface} /></ErrorBoundary>}
          {state.activeTab === "kanban"    && <ErrorBoundary tabName="Kanban">     <KanbanTab state={scopedState} dispatch={dispatch} /></ErrorBoundary>}
          {state.activeTab === "risk"      && <ErrorBoundary tabName="Risk Matrix"><RiskMatrixTab state={scopedState} dispatch={dispatch} /></ErrorBoundary>}
          {state.activeTab === "timeline"  && <ErrorBoundary tabName="Timeline">   <TimelineTab state={scopedState} dispatch={dispatch} /></ErrorBoundary>}
          {state.activeTab === "protocols" && <ErrorBoundary tabName="Protocols">  <ProtocolMatrixTab state={scopedState} dispatch={dispatch} /></ErrorBoundary>}
          {state.activeTab === "visual"    && <ErrorBoundary tabName="Architecture"><VisualSelection state={scopedState} dispatch={dispatch} onInterfaceSelect={handleInterfaceSelect} selectedInterfaceId={state.selectedInterface} setSelectedInterface={(id) => dispatch({ type: "SET_SELECTED_INTERFACE", interfaceId: id })} /></ErrorBoundary>}
          {state.activeTab === "export"    && <ErrorBoundary tabName="Export">     <ExportTab state={scopedState} dispatch={dispatch} /></ErrorBoundary>}
          {state.activeTab === "e2e"       && <ErrorBoundary tabName="E2E Gaps">   <E2EClm /></ErrorBoundary>}

          {/* ─── NEW: 8 slidedeck-derived tabs ────────────────────── */}
          {state.activeTab === "master"    && <ErrorBoundary tabName="Master Architecture"><MasterArchitectureTab /></ErrorBoundary>}
          {state.activeTab === "agentic"   && <ErrorBoundary tabName="Agentic AI">         <AgenticAITab state={state} dispatch={dispatch} /></ErrorBoundary>}
          {state.activeTab === "benefits"  && <ErrorBoundary tabName="Benefits">           <BenefitsTab state={state} dispatch={dispatch} /></ErrorBoundary>}
          {state.activeTab === "esb"       && <ErrorBoundary tabName="ESB Integration">    <ESBIntegrationTab /></ErrorBoundary>}
          {state.activeTab === "dfd"       && <ErrorBoundary tabName="Unified DFD">        <UnifiedDFDTab /></ErrorBoundary>}
          {state.activeTab === "contracts" && <ErrorBoundary tabName="Data Contracts">     <DataContractsTab /></ErrorBoundary>}
          {state.activeTab === "lld"       && <ErrorBoundary tabName="LLD Deep-Dive">      <LLDTab state={scopedState} /></ErrorBoundary>}
          {state.activeTab === "intel"     && <ErrorBoundary tabName="Intelligence">       <InterfaceIntelligenceTab /></ErrorBoundary>}
          {state.activeTab === "plan"      && <ErrorBoundary tabName="Implementation Plan"><ImplementationPlanTab state={scopedState}/></ErrorBoundary>}
          {state.activeTab === "taxonomy"  && <ErrorBoundary tabName="Interface Taxonomy"> <InterfaceTaxonomyTab /></ErrorBoundary>}
          {state.activeTab === "b2mml"     && <ErrorBoundary tabName="B2MML Federation">   <B2MMLFederationTab /></ErrorBoundary>}
          {state.activeTab === "syncGuide" && <ErrorBoundary tabName="User Sync Guide">    <UserSyncGuideTab /></ErrorBoundary>}
        </div>
      </div>
    </div>
    </NavigationProvider>
  );
}
